import React from 'react';
import axios from 'axios';
import { T, MONO, FONT, SERIF, radius, tint, alpha } from './poyais-ui/theme';
import { fmt_n, fmt_k, fmt_roas, fmt_sec, fmt_pct, pct_dir, isBrandCampaign } from './poyais-ui/format';
import { useIsMobile } from './poyais-ui/hooks';
import { Icon } from './poyais-ui/icons';
import { Sparkline, Donut, ChartToggle } from './poyais-ui/charts';
import { Card, Tile, Grid, SectionHeader, Stat, Badge, Progress, Button, IconChip, LiveDot, DeltaPill, KpiCard as KitKpiCard } from './poyais-ui/primitives';
import { DateFilterBox } from './poyais-ui/DateFilter';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';
import { openedLabel } from './Gorevler';

addTranslations({
  'Reklam Geliri': 'Ad Revenue',
  'Para birimleri karışık — toplanmaz': 'Mixed currencies — not added up',
  'Blended ROAS': 'Blended ROAS',
  'KANAL PERFORMANSI': 'CHANNEL PERFORMANCE',
  'Detaylar': 'Details',
  'VERİLER YÜKLENİYOR': 'LOADING DATA',
  'Canlı Veri': 'Live Data',
  'Veriler toplandıkça buraya haftalık performans özeti gelecek.': 'As data accumulates, your weekly performance summary will appear here.',
  'AI Asistana Sor': 'Ask the AI Assistant',
  'Reklam Harcaması': 'Ad Spend',
  'Genel': 'Overall',
  'Marka': 'Brand',
  'Marka Dışı': 'Non-Brand',
  'Marka kampanyaları': 'Brand campaigns',
  'Marka dışı kampanyalar': 'Non-brand campaigns',
  'Önceki döneme göre.': 'vs. previous period.',
  'Gelir': 'Revenue',
  'Satın Alma': 'Purchases',
  'Kampanya Performansı': 'Campaign Performance',
  'Tüm Kampanyalar': 'All Campaigns',
  "Bu grafiği görmek için Ayarlar'dan bir reklam platformu bağlayın.": 'Connect an ad platform from Settings to see this chart.',
  'Toplam': 'Total',
  'Grafiği görmek için en az bir platform seçin.': 'Select at least one platform to see the chart.',
  'Bu dönem için kampanya verisi yok.': 'No campaign data for this period.',
  'Bu dönem için kanal verisi yok.': 'No channel data for this period.',
  'harcama': 'spend',
  'Tümünü Gör': 'See all',
  'Bekleyen görev yok.': 'No pending tasks.',
  'Yüksek': 'High',
  'Orta': 'Medium',
  'Düşük': 'Low',
  'Yeni Görev': 'New Task',
  'KPI Hedefleri': 'KPI Goals',
  'Kapat': 'Close',
  'Hedef Ekle': 'Add Goal',
  'Metrik': 'Metric',
  'Harcama (₺)': 'Spend (₺)',
  'Dönüşüm': 'Conversions',
  'Tümü': 'All',
  'Hedef Değer': 'Target Value',
  'Örn: 5': 'e.g. 5',
  'Kaydet': 'Save',
  'Hedef': 'Target',
  'Kaldır': 'Remove',
  'Sinyaller': 'Signals',
  'Tüm Sinyaller': 'All Signals',
  'Şu an için sinyal yok.': 'No signals right now.',
  "Bu grafiği görmek için Ayarlar'dan Google Analytics'i bağlayın.": 'Connect Google Analytics from Settings to see this chart.',
  'Bağlı kaynaklar': 'Connected sources',
  'bağlı.': 'connected.',
  'Henüz platform bağlı değil.': 'No platform connected yet.',
});

const API = '/api/v1';

function greetingWord(lang:'tr'|'en'):string{
  const h=new Date().getHours();
  if(lang==='en'){ if(h<12)return 'Good morning'; if(h<18)return 'Good afternoon'; return 'Good evening'; }
  if(h<12)return 'Günaydın';if(h<18)return 'İyi günler';return 'İyi akşamlar';
}

function fmt_sec_diff(curr:number,prev:number):string{if(!prev||!curr)return '—';const d=curr-prev;const sign=d>=0?'+':'-';const abs=Math.abs(d);const m=Math.floor(abs/60);const s=Math.round(abs%60);return `${sign}${m}:${s.toString().padStart(2,'0')}`;}

// ── Mini Area Chart (kept local for hover-tooltip UX; retthemed to kit tokens) ──
function MiniAreaChart({data,color,labels,tooltips,height:hProp,fmt:fmtProp,axisFmt}:{data:number[];color:string;labels:string[];tooltips?:number[];height?:number;fmt?:(v:number)=>string;axisFmt?:(v:number)=>string}){
  const w=500,h=hProp||100,padL=32,padB=20,padR=4,padT=4;
  const W=w-padL-padR,H=h-padT-padB;
  const max=Math.max(...data),min=0,range=max-min||1;
  // "Bu Ay"/"Geçen Ay" seçiminde data.length===1 olabiliyor — data.length-1===0 bölme
  // NaN koordinat üretip SVG'yi kırıyordu, tek noktayı artık grafik alanının ortasına
  // sabitliyoruz.
  const px=(i:number)=>padL+(data.length>1?(i/(data.length-1))*W:W/2);
  const py=(v:number)=>padT+H-((v-min)/range)*H;
  const pts=data.map((v,i)=>`${px(i)},${py(v)}`).join(' ');
  const fillPts=`${padL},${padT+H} ${pts} ${padL+W},${padT+H}`;
  const [hov,setHov]=React.useState<number|null>(null);
  const fmt=fmtProp||((v:number)=>v>=1000000?'₺'+(v/1000000).toFixed(1)+'M':v>=1000?'₺'+(v/1000).toFixed(0)+'K':'₺'+v);
  // color artık ham hex değil, çoğunlukla "var(--py-accent)" gibi bir token — parantez/tire
  // içeren gradient id'si geçersiz bir url(#...) referansı üretiyordu ve SVG polygon
  // varsayılan SİYAH dolguya düşüyordu. id'yi sadece harf/rakama indirgiyoruz.
  const gid='mg_'+color.replace(/[^a-zA-Z0-9]/g,'');
  return(
    <div style={{position:'relative'}}>
    {hov!==null&&(
      <div style={{position:'absolute',top:0,left:`${data.length>1?(hov/(data.length-1))*100:50}%`,transform:'translateX(-50%)',background:'#1A1A2E',color:'#fff',fontSize:10,padding:'4px 8px',borderRadius:6,whiteSpace:'nowrap',zIndex:10,pointerEvents:'none',fontFamily:MONO}}>
        {labels[hov]} — {fmt(data[hov])}{tooltips&&tooltips[hov]?' · '+tooltips[hov].toFixed(2)+'x ROAS':''}
      </div>
    )}
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" onMouseLeave={()=>setHov(null)}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {[0,0.5,1].map(f=>(
        <line key={f} x1={padL} y1={padT+H*(1-f)} x2={padL+W} y2={padT+H*(1-f)}
          stroke={T.border} strokeWidth="1" strokeDasharray="3 4"/>
      ))}
      {(()=>{
        // Değer aralığı çok küçükse (ör. hepsi sıfıra yakın), 3 eksen etiketi hepsi aynı
        // yuvarlanmış metne ("₺0K" gibi) düşüyordu ve üst üste tekrar ediyormuş gibi
        // görünüyordu — art arda aynı metni üreten etiketler burada eleniyor, her metin
        // sadece bir kez gösteriliyor.
        const seen=new Set<string>();
        return [1,0.5,0].map(f=>{
          const label=axisFmt?axisFmt(min+range*f):`${Math.round((min+range*f)/1000)}K`;
          if(seen.has(label))return null;
          seen.add(label);
          return(
            <text key={f} x={padL-4} y={padT+H*(1-f)+4} textAnchor="end"
              fill={T.textDim} fontSize="8" fontFamily="monospace">
              {label}
            </text>
          );
        });
      })()}
      <polygon points={fillPts} fill={`url(#${gid})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {data.map((_,i)=>(
        <rect key={i} x={px(i)-8} y={0} width={16} height={h} fill="transparent"
          onMouseEnter={()=>setHov(i)} style={{cursor:'pointer'}}/>
      ))}
      {hov!==null&&(
        <line x1={px(hov)} y1={padT} x2={px(hov)} y2={padT+H} stroke={T.text} strokeWidth="1" strokeDasharray="3 3" opacity="0.3"/>
      )}
    </svg>
    <div style={{display:'flex',justifyContent:'space-between',marginTop:6,padding:'0 2px'}}>
      {labels.map((l,i)=>(
        <span key={i} style={{fontSize:8.5,color:T.textDim,fontFamily:MONO,whiteSpace:'nowrap' as const,flexShrink:0}}>{l}</span>
      ))}
    </div>
    </div>
  );
}

// ── Mini Bar Chart (kept local for hover-tooltip UX; retthemed to kit tokens) ──
function MiniBarChart({data,color,labels,tooltips,fmt:fmtProp}:{data:number[];color:string;labels:string[];tooltips?:number[];fmt?:(v:number)=>string}){
  const [hov,setHov]=React.useState<number|null>(null);
  const max=Math.max(...data,1),padB=20,padT=4,H=100-padB-padT;
  const fmt=fmtProp||((v:number)=>v>=1000000?'₺'+(v/1000000).toFixed(1)+'M':v>=1000?'₺'+(v/1000).toFixed(0)+'K':'₺'+v);
  return(
    <div style={{position:'relative'}}>
      {hov!==null&&(
        <div style={{position:'absolute',top:-32,left:`${(hov/(data.length-1))*100}%`,transform:'translateX(-50%)',background:'#1A1A2E',color:'#fff',fontSize:10,padding:'4px 8px',borderRadius:6,whiteSpace:'nowrap',zIndex:10,pointerEvents:'none',fontFamily:MONO}}>
          {labels[hov]} — {fmt(data[hov])}{tooltips&&tooltips[hov]?' · '+tooltips[hov].toFixed(2)+'x ROAS':''}
        </div>
      )}
      <svg width="100%" height={100} viewBox={`0 0 ${data.length*16} ${100}`} preserveAspectRatio="none"
        onMouseLeave={()=>setHov(null)}>
        {data.map((v,i)=>{
          const bh=(v/max)*H,x=i*16+2,y=padT+H-bh;
          const c=hov===i?color:i===data.length-1?color:alpha(color,27);
          return(
            <g key={i} onMouseEnter={()=>setHov(i)} style={{cursor:'pointer'}}>
              <rect x={x} y={padT} width={12} height={H} rx="3" fill={T.border} opacity="0.5"/>
              <rect x={x} y={y} width={12} height={bh} rx="3" fill={c}/>
            </g>
          );
        })}
      </svg>
      {labels&&(
      <div style={{display:'flex',justifyContent:'space-between',marginTop:6,padding:'0 2px'}}>
        {labels.map((l,i)=>(
          <span key={i} style={{fontSize:8.5,color:T.textDim,fontFamily:MONO,whiteSpace:'nowrap' as const,flexShrink:0}}>{l}</span>
        ))}
      </div>
      )}
    </div>
  );
}

// ── Connect Banner ────────────────────────────────────────
// "Tüm kanallar bağlı" mesajı/CTA mantığı bilerek sadece çekirdek reklam hesaplarına bakıyor
// (ChatGPT Ads/Adjust bağlı olmasa da banner "eksik" durumunda takılı kalmasın; Trello bir
// görev panosu, Shopify bir e-ticaret platformu — reklam hesabı değiller, bu banner'da hiç yer almazlar).
const CONNECT_PLATFORMS=[
  {key:'ga4',  name:'GA4',          icon:'/google-analytics-icon.png', dot:'var(--py-orange-text)'},
  {key:'gads', name:'Google Ads',   icon:'/google-ads-icon.png',       dot:T.gold},
  {key:'meta', name:'Meta Ads',     icon:'/meta-icon.png',             dot:T.purple},
];
// Rozet sırası + "Bağlı kaynaklar" metnindeki isim eşlemesi için tam reklam-platformu listesi —
// ChatGPT Ads/Adjust burada var (bağlantı zorunlu değil ama bağlıysa gösterilir/isimlenir).
const AD_PLATFORMS=[
  ...CONNECT_PLATFORMS,
  {key:'chatgpt_ads', name:'ChatGPT Ads', icon:'/chatgpt-icon.png',    dot:T.green},
  {key:'adjust', name:'Adjust',     icon:'/adjust-icon.png',           dot:T.amber},
];

function ConnectBanner({bagliPlatformlar,recsCount,onNavigate,onDismiss}:{bagliPlatformlar:string[];recsCount:number;onNavigate?:(v:string)=>void;onDismiss?:()=>void}){
  const { lang } = useLang();
  const bagli=bagliPlatformlar||[];
  const missing=CONNECT_PLATFORMS.filter(p=>!bagli.includes(p.key));
  const allConnected=missing.length===0;

  let title:string, subtitle:string, buttonLabel:string;
  const onClick=()=>onNavigate?.(allConnected?'poyaisone-recommendations':'settings');
  const allNames=CONNECT_PLATFORMS.map(p=>p.name).join(', ');
  if(lang==='en'){
    if(allConnected){
      title='All channels connected.';
      subtitle=`${recsCount} new suggestion${recsCount===1?'':'s'} ready. ${allNames} data is up to date.`;
      buttonLabel='View Suggestions';
    }else if(bagli.length===0){
      title='Connect your ad accounts.';
      subtitle=`Connect ${allNames} to unlock your performance summary, KPIs, and campaign insights.`;
      buttonLabel='Connect Accounts';
    }else{
      const bagliNames=CONNECT_PLATFORMS.filter(p=>bagli.includes(p.key)).map(p=>p.name).join(', ');
      const missingNames=missing.map(p=>p.name).join(', ');
      title='Some channels are not connected yet.';
      subtitle=`${bagliNames} data is up to date. ${missingNames} ${missing.length===1?'is':'are'} not connected yet.`;
      buttonLabel='Connect Accounts';
    }
  }else if(allConnected){
    title='Tüm kanallar bağlı.';
    subtitle=`${recsCount} yeni öneri hazır. ${allNames} verileri güncellendi.`;
    buttonLabel='Önerileri Gör';
  }else if(bagli.length===0){
    title='Reklam hesaplarını bağla.';
    subtitle=`${allNames} bağla; performans özetini, KPI'ları ve kampanya içgörülerini aç.`;
    buttonLabel='Hesapları Bağla';
  }else{
    const bagliNames=CONNECT_PLATFORMS.filter(p=>bagli.includes(p.key)).map(p=>p.name).join(', ');
    const missingNames=missing.map(p=>p.name).join(', ');
    title='Bazı kanallar henüz bağlı değil.';
    subtitle=`${bagliNames} verileri güncelleniyor. ${missingNames} henüz bağlı değil.`;
    buttonLabel='Hesapları Bağla';
  }

  return(
    <div style={{position:'relative' as const,display:'flex',alignItems:'center',gap:22,flexWrap:'wrap' as const,border:'1px solid var(--py-border)',borderRadius:16,padding:'20px 24px',
      backgroundColor:'var(--py-card)',backgroundImage:'radial-gradient(var(--py-border-mid) 1px, transparent 1px)',backgroundSize:'14px 14px'}}>
      {allConnected && onDismiss && (
        <button onClick={onDismiss} style={{position:'absolute' as const,top:10,right:10,width:24,height:24,borderRadius:7,border:'none',
          background:'transparent',color:'var(--py-text-faint)',cursor:'pointer',fontSize:13,lineHeight:1,display:'flex',alignItems:'center',justifyContent:'center'}}
          title={lang==='en'?'Dismiss':'Kapat'}>✕</button>
      )}
      <div style={{display:'flex',alignItems:'center',flexShrink:0}}>
        {AD_PLATFORMS.map((p,i)=>(
          <span key={p.key} title={p.name} style={{position:'relative' as const,width:38,height:38,borderRadius:'50%',background:'var(--py-surface)',border:'1px solid var(--py-divider)',
            boxShadow:'0 1px 2px rgba(20,22,43,.06)',display:'flex',alignItems:'center',justifyContent:'center',
            marginLeft:i?-9:0,opacity:bagli.includes(p.key)?1:0.35}}>
            <img src={p.icon} width={19} height={19} style={{objectFit:'contain' as const}} alt={p.name}/>
            <span style={{position:'absolute' as const,bottom:-1,right:-1,width:10,height:10,borderRadius:'50%',
              background:bagli.includes(p.key)?p.dot:'var(--py-border-soft)',border:'1.5px solid var(--py-surface)'}}/>
          </span>
        ))}
      </div>
      <div style={{flex:1,minWidth:240}}>
        <div style={{fontSize:15.5,fontWeight:800,letterSpacing:'-0.015em',marginBottom:4}}>{title}</div>
        <div style={{fontSize:13,color:'var(--py-text-mid-alt)'}}>{subtitle}</div>
      </div>
      <button onClick={onClick} style={{display:'inline-flex',alignItems:'center',gap:8,
        background:T.accent,color:'#fff',border:'none',borderRadius:11,padding:'9px 16px',fontFamily:'inherit',fontSize:13,fontWeight:700,cursor:'pointer',flexShrink:0}}>
        {buttonLabel}
      </button>
    </div>
  );
}

const MONTHS=['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
const MONTHS_EN=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const monthNames=(lang:'tr'|'en')=>lang==='en'?MONTHS_EN:MONTHS;
const SPARK_DATA=[40,60,55,75,65,90,100,85,70,95,60,80,75,90];

// ── Channel Card ──────────────────────────────────────────
function ChannelCard({label,color,metrics,onNavigate,navKey,chartType,setChartType,chartData,chartLabels,chartTooltipData}:{label:string;color:string;metrics:{l:string;v:string;accent?:string}[];onNavigate?:(v:string)=>void;navKey:string;chartType:'area'|'bar';setChartType:(t:'area'|'bar')=>void;chartData?:number[];chartLabels?:string[];chartTooltipData?:number[]}){
  const { t: tt, lang } = useLang();
  const data=chartData||SPARK_DATA.slice(0,12);
  const labels=chartLabels||monthNames(lang).slice(0,data.length);
  const tooltips=chartTooltipData;
  return(
    <Card pad={20}>
      <SectionHeader
        eyebrow={tt('KANAL PERFORMANSI')}
        title={label}
        right={
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            <ChartToggle value={chartType} onChange={setChartType}/>
            <Button variant="soft" color={color} onClick={()=>onNavigate?.(navKey)}>
              {tt('Detaylar')} <Icon.external size={11}/>
            </Button>
          </div>
        }
      />
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:8,marginBottom:14}}>
        {metrics.map((m,i)=>(
          <Tile key={i} pad={12}>
            <Stat label={m.l} value={m.v} color={m.accent||T.text} size={16}/>
          </Tile>
        ))}
      </div>
      <div style={{height:'auto'}}>
        {chartType==='area'
          ?<MiniAreaChart data={data} color={color} labels={labels} tooltips={tooltips}/>
          :<MiniBarChart data={data} color={color} labels={labels} tooltips={tooltips}/>
        }
      </div>
    </Card>
  );
}

// ── Main ──────────────────────────────────────────────────
export default function DashboardTab({start,end,prevStart,prevEnd,config,configLoaded,onNavigate,clientId,onDateChange,sidebarOpen}:{start:string;end:string;prevStart:string;prevEnd:string;config:any;configLoaded?:boolean;onNavigate?:(v:string)=>void;clientId?:number;onDateChange?:(s:string,e:string)=>void;sidebarOpen?:boolean}){
  const isMobile=useIsMobile();
  const { t: tt, lang } = useLang();
  const [ga4,setGa4]=React.useState<any>(null);
  // Bağlı hesapların para birimi / saat dilimi + mağaza geliri (blended ROAS için)
  const [acctCtx,setAcctCtx]=React.useState<any>(null);
  React.useEffect(()=>{
    if(!clientId){setAcctCtx(null);return;}
    let alive=true;
    axios.get(`${API}/analysis/context/${clientId}`,{params:{start_date:start,end_date:end}}).then(r=>{if(alive)setAcctCtx(r.data||null);}).catch(()=>{if(alive)setAcctCtx(null);});
    return ()=>{alive=false;};
  },[clientId,start,end]);
  const [gads,setGads]=React.useState<any>(null);
  const [meta,setMeta]=React.useState<any>(null);
  const [prevGa4,setPrevGa4]=React.useState<any>(null);
  const [prevGads,setPrevGads]=React.useState<any>(null);
  const [prevMeta,setPrevMeta]=React.useState<any>(null);
  const [campaigns,setCampaigns]=React.useState<{platform:'gads'|'meta';name:string;spend:number;roas:number;conversions:number;revenue:number;impressions:number;status:string}[]>([]);
  // Brand/NonBrand/Overall ROAS segmentasyonu için — tüm kampanyaların (top-4'e kesilmeden
  // önceki) spend/revenue toplamı, isBrandCampaign (poyais-ui/format.ts) ile ikiye ayrılmış.
  // Tool özelliği: tüm müşteriler için geçerli (kullanıcı isteği, 2026-09-13) — "ROAS
  // kartına branding kampanyaları da dahil oluyor, yanlış" şikayeti üzerine.
  const [roasSegment,setRoasSegment]=React.useState<'overall'|'brand'|'nonbrand'>('overall');
  const [roasMenuOpen,setRoasMenuOpen]=React.useState(false);
  // Reklam Harcaması/Gelir/Satın Alma kartları da ROAS'la aynı Genel/Marka/Marka Dışı
  // filtresini alıyor (kullanıcı isteği, 2026-09-16) — her kart kendi bağımsız segment+menü
  // state'ine sahip, aynı ROAS deseninin tekrarı.
  const [spendSegment,setSpendSegment]=React.useState<'overall'|'brand'|'nonbrand'>('overall');
  const [spendMenuOpen,setSpendMenuOpen]=React.useState(false);
  const [revenueSegment,setRevenueSegment]=React.useState<'overall'|'brand'|'nonbrand'>('overall');
  const [revenueMenuOpen,setRevenueMenuOpen]=React.useState(false);
  const [convSegment,setConvSegment]=React.useState<'overall'|'brand'|'nonbrand'>('overall');
  const [convMenuOpen,setConvMenuOpen]=React.useState(false);
  const [brandSpend,setBrandSpend]=React.useState(0);
  const [brandRevenue,setBrandRevenue]=React.useState(0);
  const [nonBrandSpend,setNonBrandSpend]=React.useState(0);
  const [nonBrandRevenue,setNonBrandRevenue]=React.useState(0);
  const [brandConversions,setBrandConversions]=React.useState(0);
  const [nonBrandConversions,setNonBrandConversions]=React.useState(0);
  // Önceki dönemin Marka/Marka Dışı toplamları — segment seçiliyken de "önceki döneme göre" farkı gösterilsin diye.
  const [prevSeg,setPrevSeg]=React.useState({brandSpend:0,brandRevenue:0,brandConv:0,nonBrandSpend:0,nonBrandRevenue:0,nonBrandConv:0});
  const [loading,setLoading]=React.useState(true);
  const [mounted,setMounted]=React.useState(false);
  const [chartType,setChartType]=React.useState<'area'|'bar'>('bar');
  // Kampanya Performansı grafiği: platform seçimi + gösterilen nitelik, müşteri başına
  // localStorage'da saklanıyor (FaturalamaPage'in kart seçimini sakladığı desenle aynı).
  const kampStorageKey=clientId?`poyais_kampanya_perf_${clientId}`:'';
  const [kampPlatforms,setKampPlatforms]=React.useState<string[]>(['gads','meta']);
  const [kampMetric,setKampMetric]=React.useState<'spend'|'conversions'|'roas'|'revenue'|'impressions'>('spend');
  React.useEffect(()=>{
    if(!kampStorageKey)return;
    try{
      const saved=JSON.parse(localStorage.getItem(kampStorageKey)||'null');
      if(saved&&Array.isArray(saved.platforms))setKampPlatforms(saved.platforms);
      if(saved&&saved.metric)setKampMetric(saved.metric);
    }catch(e){/* bozuk kayıt varsa varsayılana düş */}
  },[kampStorageKey]);
  const kampToggle=(key:string)=>{
    setKampPlatforms(prev=>{
      const next=prev.includes(key)?prev.filter(p=>p!==key):[...prev,key];
      if(kampStorageKey)localStorage.setItem(kampStorageKey,JSON.stringify({platforms:next,metric:kampMetric}));
      return next;
    });
  };
  const kampSetMetric=(m:typeof kampMetric)=>{
    setKampMetric(m);
    if(kampStorageKey)localStorage.setItem(kampStorageKey,JSON.stringify({platforms:kampPlatforms,metric:m}));
  };
  // Analytics kartı (GA4): Kampanya Performansı ile aynı desen — metrik seçimi müşteri
  // başına localStorage'da saklanıyor, tıklanınca grafik değişiyor.
  const gaStorageKey=clientId?`poyais_ga4_dashboard_${clientId}`:'';
  const [gaMetric,setGaMetric]=React.useState<'sessions'|'purchases'|'revenue'|'cr'>('sessions');
  React.useEffect(()=>{
    if(!gaStorageKey)return;
    try{
      const saved=localStorage.getItem(gaStorageKey);
      if(saved)setGaMetric(saved as typeof gaMetric);
    }catch(e){/* bozuk kayıt varsa varsayılana düş */}
  },[gaStorageKey]);
  const gaSetMetric=(m:typeof gaMetric)=>{
    setGaMetric(m);
    if(gaStorageKey)localStorage.setItem(gaStorageKey,m);
  };
  // Kampanya Performansı ve Analytics kartlarının her biri kendi ay aralığı seçimine
  // sahip (birbirinden bağımsız). "6 Ay"/"12 Ay" zaten çekilmiş 12 aylık seriden
  // client-side slice; "Bu Ay"/"Geçen Ay" ise GÜNLÜK seriye geçiyor (tek aylık nokta
  // "dağ" gibi anlamsız görünüyordu) — bunun için ayrı /daily uçları çağrılıyor.
  type ChartRange='this'|'last'|'6m'|'12m';
  const [kampRange,setKampRange]=React.useState<ChartRange>('12m');
  const [gaRange,setGaRange]=React.useState<ChartRange>('12m');
  const isDailyRange=(r:ChartRange)=>r==='this'||r==='last';
  const RANGE_LABEL_SHORT:Record<ChartRange,string> = lang==='en'
    ? {this:'This Month',last:'Last Month','6m':'6 Months','12m':'12 Months'}
    : {this:'Bu Ay',last:'Geçen Ay','6m':'6 Ay','12m':'12 Ay'};
  const RANGE_LABEL_LONG:Record<ChartRange,string> = lang==='en'
    ? {this:'This Month',last:'Last Month','6m':'Last 6 Months','12m':'Last 12 Months'}
    : {this:'Bu Ay',last:'Geçen Ay','6m':'Son 6 Ay','12m':'Son 12 Ay'};
  // Dropdown — mobildeki metrik <select>'i ile aynı stil, "Devamını Gör" tarzı pill
  // grubu yerine (kullanıcı isteği: hem masaüstü hem mobilde dropdown, soldaki
  // metrik seçiciyle aynı satır/hizada).
  function RangeSwitcher({value,onChange}:{value:ChartRange;onChange:(r:ChartRange)=>void}){
    return (
      <select value={value} onChange={e=>onChange(e.target.value as ChartRange)}
        style={{padding:'6px 9px',borderRadius:9,border:'1px solid var(--py-border-mid)',background:'var(--py-surface)',
        fontSize:11,fontWeight:700,color:'var(--py-text)',fontFamily:'inherit',cursor:'pointer',flexShrink:0}}>
        {(['this','last','6m','12m'] as ChartRange[]).map(r=>(
          <option key={r} value={r}>{RANGE_LABEL_SHORT[r]}</option>
        ))}
      </select>
    );
  }
  // "Bu Ay"/"Geçen Ay" için tarih sınırları — Bu Ay: ayın 1'inden bugüne; Geçen Ay:
  // önceki ayın tamamı.
  const fmtISODate=(d:Date)=>`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const monthBounds=(which:'this'|'last'):{start:string;end:string}=>{
    // "Bugün" hesabın saat diliminde (reklam hesabı/GA4); bilinmiyorsa tarayıcı saati.
    let now=new Date();
    const tz=(Object.values(acctCtx?.timezones||{}).find((z:any)=>typeof z==='string'&&z) as string|undefined);
    if(tz){try{const [y,m,d]=new Intl.DateTimeFormat('en-CA',{timeZone:tz}).format(now).split('-').map(Number);now=new Date(y,m-1,d);}catch{}}
    if(which==='this')return{start:fmtISODate(new Date(now.getFullYear(),now.getMonth(),1)),end:fmtISODate(now)};
    return{start:fmtISODate(new Date(now.getFullYear(),now.getMonth()-1,1)),end:fmtISODate(new Date(now.getFullYear(),now.getMonth(),0))};
  };
  const enumerateDays=(start:string,end:string):string[]=>{
    const out:string[]=[];let d=new Date(start+'T00:00:00');const endD=new Date(end+'T00:00:00');
    while(d<=endD){out.push(fmtISODate(d));d=new Date(d.getFullYear(),d.getMonth(),d.getDate()+1);}
    return out;
  };
  const [kampDailyByPlatform,setKampDailyByPlatform]=React.useState<Record<string,any[]>>({});
  const [gaDaily,setGaDaily]=React.useState<any[]>([]);
  const [ga4Monthly,setGa4Monthly]=React.useState<any[]>([]);
  const [ga4Channels,setGa4Channels]=React.useState<any[]>([]);
  // "Tüm kanallar bağlı" kartı — sadece onboarding amaçlı: tüm çekirdek kanallar bağlanınca
  // bir kez "bağlandı" mesajı gösterip kapat (✕) ile kalıcı olarak kapatılabiliyor, artık
  // yeni önerilere (recs) göre tekrar tekrar geri gelmiyor (kullanıcı bunu "bağlı müşteride
  // kalıcı olarak kaybolsun" diye istedi).
  const connectBannerDismissKey=clientId?`poyais_connect_banner_dismissed_${clientId}`:'';
  const [connectBannerDismissed,setConnectBannerDismissed]=React.useState(false);
  React.useEffect(()=>{
    if(!connectBannerDismissKey){setConnectBannerDismissed(false);return;}
    try{setConnectBannerDismissed(localStorage.getItem(connectBannerDismissKey)==='1');}catch(e){/* localStorage yoksa varsayılan: gösterilsin */}
  },[connectBannerDismissKey]);
  const dismissConnectBanner=()=>{
    setConnectBannerDismissed(true);
    if(connectBannerDismissKey)try{localStorage.setItem(connectBannerDismissKey,'1');}catch(e){/* sessizce geç */}
  };
  const [miniTasks,setMiniTasks]=React.useState<any[]>([]);
  React.useEffect(()=>{
    const cid=clientId||1;
    axios.get(`${API}/gorevler/?client_id=${cid}`).then(r=>setMiniTasks((r.data||[]).filter((t:any)=>t.status!=='done').slice(0,4))).catch(()=>{});
  },[clientId]);
  const [gadsMonthly,setGadsMonthly]=React.useState<any[]>([]);
  const [metaMonthly,setMetaMonthly]=React.useState<any[]>([]);
  const [chatgptMonthly,setChatgptMonthly]=React.useState<any[]>([]);
  const [criteoMonthly,setCriteoMonthly]=React.useState<any[]>([]);
  const [adjustMonthly,setAdjustMonthly]=React.useState<any[]>([]);
  const [rtbhouseMonthly,setRtbhouseMonthly]=React.useState<any[]>([]);
  const [recs,setRecs]=React.useState<any[]>([]);
  const [goals,setGoals]=React.useState<any[]>([]);
  const [goalsOpen,setGoalsOpen]=React.useState(false);
  const [newGoal,setNewGoal]=React.useState({metric:'roas',target:'',platform:'all'});
  // Hedef düzenlenirken orijinal (metric,platform) çifti — kaydedince platform değiştiyse
  // eski satır silinmeli, aksi halde backend'in ON CONFLICT(user_id,platform,metric)
  // anahtarı platformu da kapsadığı için "düzenleme" aslında yeni bir satır ekliyor, eskisi
  // olduğu yerde kalıyor (kullanıcı canlıda yakaladı: ROAS hedefini Google'dan Tümü'ne
  // çevirip kaydetti, eski Google satırı silinmediği için hâlâ 0.66 gösteren eski kart
  // da listede kalmaya devam etti).
  const [editingGoal,setEditingGoal]=React.useState<{metric:string;platform:string}|null>(null);
  const [perfInsight,setPerfInsight]=React.useState<string|null>(null);
  React.useEffect(()=>{
    const cid=clientId||1;
    const days=Math.max(1,Math.round((new Date(end).getTime()-new Date(start).getTime())/86400000)+1);
    axios.get(`${API}/optimize/performance-summary?client_id=${cid}&days=${days}&lang=${lang}&start_date=${start}&end_date=${end}`)
      .then(r=>setPerfInsight(r.data?.insight||null)).catch(()=>setPerfInsight(null));
  },[clientId,start,end,lang]);

  const platforms=config?.platforms||{};
  const ga4Cfg=platforms.ga4||{};
  React.useEffect(()=>{
    if(!(ga4Cfg.propertyId && ga4Cfg.enabled!==false)){setGa4Channels([]);return;}
    axios.get(`${API}/ga4/${ga4Cfg.propertyId}/channels?start_date=${start}&end_date=${end}`).catch(()=>null)
      .then(r=>setGa4Channels(Array.isArray(r?.data)?r!.data:[]));
  },[ga4Cfg.propertyId,ga4Cfg.enabled,start,end]);
  const gadsCfg=platforms.gads||{};
  const metaCfg=platforms.meta||{};
  // "Bu Ay"/"Geçen Ay" seçilince Google Ads + Meta için günlük seri çekilir. Not: Criteo/
  // Adjust/RTB House/ChatGPT Ads'in henüz /daily uçları yok — bu platformlar günlük
  // görünümde toplamına dahil edilemiyor (aylık görünümde dahil oluyorlar).
  React.useEffect(()=>{
    if(!isDailyRange(kampRange))return;
    const {start,end}=monthBounds(kampRange as 'this'|'last');
    const reqs:Promise<{key:string;rows:any[]}>[]=[];
    if(gadsCfg.customerId && gadsCfg.enabled!==false){
      reqs.push(axios.get(`${API}/gads/${gadsCfg.customerId}/daily`,{params:{start_date:start,end_date:end}})
        .then(r=>({key:'gads',rows:r.data||[]})).catch(()=>({key:'gads',rows:[]})));
    }
    if(metaCfg.accountId && metaCfg.enabled!==false){
      reqs.push(axios.get(`${API}/meta/${metaCfg.accountId}/daily`,{params:{start_date:start,end_date:end}})
        .then(r=>({key:'meta',rows:r.data||[]})).catch(()=>({key:'meta',rows:[]})));
    }
    Promise.all(reqs).then(results=>{
      const map:Record<string,any[]>={};
      results.forEach(r=>{map[r.key]=r.rows;});
      setKampDailyByPlatform(map);
    });
  },[kampRange,gadsCfg.customerId,gadsCfg.enabled,metaCfg.accountId,metaCfg.enabled]);
  React.useEffect(()=>{
    if(!isDailyRange(gaRange)){return;}
    if(!(ga4Cfg.propertyId && ga4Cfg.enabled!==false)){setGaDaily([]);return;}
    const {start,end}=monthBounds(gaRange as 'this'|'last');
    axios.get(`${API}/ga4/${ga4Cfg.propertyId}/daily-ecommerce`,{params:{start_date:start,end_date:end}})
      .then(r=>setGaDaily(r.data||[])).catch(()=>setGaDaily([]));
  },[gaRange,ga4Cfg.propertyId,ga4Cfg.enabled]);
  const chatgptCfg=platforms.chatgpt_ads||{};
  const adjustCfg=platforms.adjust||{};
  const criteoCfg=platforms.criteo||{};
  const rtbhouseCfg=platforms.rtbhouse||{};
  // Kapsam genişletildi: bağlıysa RTB House / Criteo / ChatGPT Ads de KPI kartlarına
  // (Reklam Harcaması/ROAS/Gelir/Satın Alma) dahil ediliyor. ChatGPT Ads'in kendi API'sinde
  // gelir/ROAS metriği yok (sadece spend+conversions) — bkz. chatgpt_ads.py'deki not.
  const [chatgpt,setChatgpt]=React.useState<any>(null);
  const [prevChatgpt,setPrevChatgpt]=React.useState<any>(null);
  const [criteo,setCriteo]=React.useState<any>(null);
  const [prevCriteo,setPrevCriteo]=React.useState<any>(null);
  const [rtbhouse,setRtbhouse]=React.useState<any>(null);
  const [prevRtbhouse,setPrevRtbhouse]=React.useState<any>(null);
  React.useEffect(()=>{
    const cid=clientId||1;
    const chatgptOn=!!chatgptCfg.accountName && chatgptCfg.enabled!==false;
    const criteoOn=!!criteoCfg.advertiserName && criteoCfg.enabled!==false;
    const rtbOn=!!rtbhouseCfg.token && rtbhouseCfg.enabled!==false;
    Promise.all([
      chatgptOn?axios.get(`${API}/chatgpt-ads/${cid}/overview`,{params:{start_date:start,end_date:end}}).catch(()=>null):Promise.resolve(null),
      chatgptOn&&prevStart&&prevEnd?axios.get(`${API}/chatgpt-ads/${cid}/overview`,{params:{start_date:prevStart,end_date:prevEnd}}).catch(()=>null):Promise.resolve(null),
      criteoOn?axios.get(`${API}/criteo/${cid}/overview`,{params:{start_date:start,end_date:end}}).catch(()=>null):Promise.resolve(null),
      criteoOn&&prevStart&&prevEnd?axios.get(`${API}/criteo/${cid}/overview`,{params:{start_date:prevStart,end_date:prevEnd}}).catch(()=>null):Promise.resolve(null),
      rtbOn?axios.get(`${API}/rtbhouse/overview`,{params:{token:rtbhouseCfg.token,day_from:start,day_to:end}}).catch(()=>null):Promise.resolve(null),
      rtbOn&&prevStart&&prevEnd?axios.get(`${API}/rtbhouse/overview`,{params:{token:rtbhouseCfg.token,day_from:prevStart,day_to:prevEnd}}).catch(()=>null):Promise.resolve(null),
    ]).then(([cg,pcg,cr,pcr,rt,prt])=>{
      setChatgpt(cg?.data||null);setPrevChatgpt(pcg?.data||null);
      setCriteo(cr?.data||null);setPrevCriteo(pcr?.data||null);
      setRtbhouse(rt?.data||null);setPrevRtbhouse(prt?.data||null);
    });
  },[clientId,start,end,prevStart,prevEnd,chatgptCfg.accountName,chatgptCfg.enabled,criteoCfg.advertiserName,criteoCfg.enabled,rtbhouseCfg.token,rtbhouseCfg.enabled]);

  const bagliPlatformlar:string[]=[];
  if((ga4Cfg.propertyId||ga4Cfg.credentials?.propertyId) && ga4Cfg.enabled!==false)bagliPlatformlar.push('ga4');
  if((gadsCfg.customerId||gadsCfg.credentials?.customerId) && gadsCfg.enabled!==false)bagliPlatformlar.push('gads');
  if((metaCfg.accountId||metaCfg.credentials?.accountId) && metaCfg.enabled!==false)bagliPlatformlar.push('meta');
  if(chatgptCfg.accountName && chatgptCfg.enabled!==false)bagliPlatformlar.push('chatgpt_ads');
  if(adjustCfg.appName && adjustCfg.enabled!==false)bagliPlatformlar.push('adjust');

  React.useEffect(()=>{
    setLoading(true);
    Promise.all([
      (ga4Cfg.propertyId && ga4Cfg.enabled!==false)?axios.get(`${API}/ga4/${ga4Cfg.propertyId}/overview?start_date=${start}&end_date=${end}`).catch(()=>null):Promise.resolve(null),
      (gadsCfg.customerId && gadsCfg.enabled!==false)?axios.get(`${API}/gads/${gadsCfg.customerId}/overview?start_date=${start}&end_date=${end}`).catch(()=>null):Promise.resolve(null),
      (metaCfg.accountId && metaCfg.enabled!==false)?axios.get(`${API}/meta/${metaCfg.accountId}/overview?start_date=${start}&end_date=${end}`).catch(()=>null):Promise.resolve(null),
      (ga4Cfg.propertyId && ga4Cfg.enabled!==false)&&prevStart&&prevEnd?axios.get(`${API}/ga4/${ga4Cfg.propertyId}/overview?start_date=${prevStart}&end_date=${prevEnd}`).catch(()=>null):Promise.resolve(null),
      (gadsCfg.customerId && gadsCfg.enabled!==false)&&prevStart&&prevEnd?axios.get(`${API}/gads/${gadsCfg.customerId}/overview?start_date=${prevStart}&end_date=${prevEnd}`).catch(()=>null):Promise.resolve(null),
      (metaCfg.accountId && metaCfg.enabled!==false)&&prevStart&&prevEnd?axios.get(`${API}/meta/${metaCfg.accountId}/overview?start_date=${prevStart}&end_date=${prevEnd}`).catch(()=>null):Promise.resolve(null),
      (gadsCfg.customerId && gadsCfg.enabled!==false)?axios.get(`${API}/gads/${gadsCfg.customerId}/campaigns?start_date=${start}&end_date=${end}`).catch(()=>null):Promise.resolve(null),
      (metaCfg.accountId && metaCfg.enabled!==false)?axios.get(`${API}/meta/${metaCfg.accountId}/campaigns?start_date=${start}&end_date=${end}`).catch(()=>null):Promise.resolve(null),
      (gadsCfg.customerId && gadsCfg.enabled!==false)&&prevStart&&prevEnd?axios.get(`${API}/gads/${gadsCfg.customerId}/campaigns?start_date=${prevStart}&end_date=${prevEnd}`).catch(()=>null):Promise.resolve(null),
      (metaCfg.accountId && metaCfg.enabled!==false)&&prevStart&&prevEnd?axios.get(`${API}/meta/${metaCfg.accountId}/campaigns?start_date=${prevStart}&end_date=${prevEnd}`).catch(()=>null):Promise.resolve(null),
    ]).then(([a,b,c,d,e,f,gCamp,mCamp,pgCamp,pmCamp])=>{
      setGa4(a?.data||null);setGads(b?.data||null);const metaRaw=c?.data;setMeta(metaRaw?.overview||metaRaw||null);setPrevGa4(d?.data||null);
      const prevMetaRaw=f?.data;setPrevGads(e?.data||null);setPrevMeta(prevMetaRaw?.overview||prevMetaRaw||null);
      const gList=Array.isArray(gCamp?.data)?gCamp?.data:(gCamp?.data?.campaigns||[]);
      const mList=Array.isArray(mCamp?.data)?mCamp?.data:(mCamp?.data?.campaigns||[]);
      const allCamps=[
        ...gList.map((c:any)=>({name:c.name||c.campaign_name||'—',spend:c.spend||0,revenue:c.revenue??(c.spend&&c.roas?c.spend*c.roas:0),conversions:c.conversions||0})),
        ...mList.map((c:any)=>({name:c.name||c.campaign_name||'—',spend:c.spend||0,revenue:c.revenue??(c.spend&&c.roas?c.spend*c.roas:0),conversions:c.purchases||0})),
      ];
      const brandCamps=allCamps.filter(c=>isBrandCampaign(c.name));
      const nonBrandCamps=allCamps.filter(c=>!isBrandCampaign(c.name));
      setBrandSpend(brandCamps.reduce((s,c)=>s+c.spend,0));
      setBrandRevenue(brandCamps.reduce((s,c)=>s+c.revenue,0));
      setNonBrandSpend(nonBrandCamps.reduce((s,c)=>s+c.spend,0));
      setNonBrandRevenue(nonBrandCamps.reduce((s,c)=>s+c.revenue,0));
      setBrandConversions(brandCamps.reduce((s,c)=>s+c.conversions,0));
      setNonBrandConversions(nonBrandCamps.reduce((s,c)=>s+c.conversions,0));
      {
        const pgList=Array.isArray(pgCamp?.data)?pgCamp?.data:(pgCamp?.data?.campaigns||[]);
        const pmList=Array.isArray(pmCamp?.data)?pmCamp?.data:(pmCamp?.data?.campaigns||[]);
        const pAll=[
          ...pgList.map((c:any)=>({name:c.name||c.campaign_name||'—',spend:c.spend||0,revenue:c.revenue??(c.spend&&c.roas?c.spend*c.roas:0),conversions:c.conversions||0})),
          ...pmList.map((c:any)=>({name:c.name||c.campaign_name||'—',spend:c.spend||0,revenue:c.revenue??(c.spend&&c.roas?c.spend*c.roas:0),conversions:c.purchases||0})),
        ];
        const pb=pAll.filter(c=>isBrandCampaign(c.name)), pn=pAll.filter(c=>!isBrandCampaign(c.name));
        const sum=(l:any[],k:'spend'|'revenue'|'conversions')=>l.reduce((s,c)=>s+c[k],0);
        setPrevSeg({brandSpend:sum(pb,'spend'),brandRevenue:sum(pb,'revenue'),brandConv:sum(pb,'conversions'),nonBrandSpend:sum(pn,'spend'),nonBrandRevenue:sum(pn,'revenue'),nonBrandConv:sum(pn,'conversions')});
      }
      const merged=[
        ...gList.map((c:any)=>({platform:'gads' as const,name:c.name||c.campaign_name||'—',spend:c.spend||0,roas:c.roas||0,conversions:c.conversions||0,revenue:c.revenue||0,impressions:c.impressions||0,status:c.status||''})),
        ...mList.map((c:any)=>({platform:'meta' as const,name:c.name||c.campaign_name||'—',spend:c.spend||0,roas:c.roas||0,conversions:c.purchases||0,revenue:c.revenue||0,impressions:c.impressions||0,status:c.status||''})),
      // Durdurulmuş/arşivlenmiş kampanyalar seçilen dönemde harcaması olsa bile listede
      // görünmemeli — ROAS'a göre sıralayınca eski, artık kapalı bir kampanya (ör. "Dpals"
      // serisi) yüksek geçmiş ROAS'ıyla listenin başına çıkabiliyordu. Backend ENABLED
      // dışındaki her şeyi (PAUSED/CAMPAIGN_PAUSED/ARCHIVED/REMOVED/...) zaten normalize
      // ediyor; status boşsa (eski/hatalı yanıt) fail-safe olarak yine dışarıda bırakılıyor.
      ].filter(c=>c.spend>0 && c.status==='ENABLED');
      setCampaigns(merged);
      setLoading(false);
    });
    // Goals
    axios.get('/api/v1/goals/').then(async r=>{
      let list = r.data||[];
      const hasConvGoal = list.some((g:any)=>g.metric==='conversions'&&g.platform==='all');
      if(!hasConvGoal){
        try{
          await axios.post('/api/v1/goals/',{metric:'conversions',target:200,platform:'all'});
          const r2 = await axios.get('/api/v1/goals/');
          list = r2.data||list;
        }catch(e){/* sessizce geç, varsayılan hedef eklenemediyse liste olduğu gibi kalsın */}
      }
      setGoals(list);
    }).catch(()=>{});
    // Recommendations
    ;(((gadsCfg.customerId && gadsCfg.enabled!==false) || (metaCfg.accountId && metaCfg.enabled!==false)) ? axios.get(`${API}/optimize/recommendations?customer_id=${gadsCfg.enabled!==false?gadsCfg.customerId||'':''}&ad_account_id=${metaCfg.enabled!==false?metaCfg.accountId||'':''}&client_id=${clientId||1}&days=30&lang=${lang}`).catch(()=>null) : Promise.resolve(null))
      .then(r=>{ if(r?.data) setRecs(r.data.slice(0,3)); });
    // Monthly breakdown
    if(gadsCfg.customerId && gadsCfg.enabled!==false){
      axios.get(`${API}/gads/${gadsCfg.customerId}/monthly?months=12`).catch(()=>null)
        .then(r=>{ if(r?.data) setGadsMonthly(r.data); });
    }
    if(ga4Cfg.propertyId && ga4Cfg.enabled!==false){
      axios.get(`${API}/ga4/${ga4Cfg.propertyId}/monthly?months=12`).catch(()=>null)
        .then(r=>{ if(r?.data) setGa4Monthly(r.data); });
    }
    if(metaCfg.accountId && metaCfg.enabled!==false){
      axios.get(`${API}/meta/${metaCfg.accountId}/monthly?months=12`).catch(()=>null)
        .then(r=>{ if(r?.data) setMetaMonthly(r.data); });
    }
    const cid=clientId||1;
    if(chatgptCfg.accountName && chatgptCfg.enabled!==false){
      axios.get(`${API}/chatgpt-ads/${cid}/monthly?months=12`).catch(()=>null)
        .then(r=>{ if(r?.data) setChatgptMonthly(r.data); });
    }
    if(criteoCfg.advertiserName && criteoCfg.enabled!==false){
      axios.get(`${API}/criteo/${cid}/monthly?months=12`).catch(()=>null)
        .then(r=>{ if(r?.data) setCriteoMonthly(r.data); });
    }
    if(adjustCfg.appName && adjustCfg.enabled!==false){
      axios.get(`${API}/adjust/${cid}/monthly?months=12`).catch(()=>null)
        .then(r=>{ if(r?.data) setAdjustMonthly(r.data); });
    }
    if(rtbhouseCfg.token && rtbhouseCfg.enabled!==false){
      axios.get(`${API}/rtbhouse/monthly`,{params:{token:rtbhouseCfg.token,months:12}}).catch(()=>null)
        .then(r=>{ if(r?.data) setRtbhouseMonthly(r.data); });
    }
  },[start,end,prevStart,prevEnd,JSON.stringify(config)]);

  React.useEffect(()=>{const t=setTimeout(()=>setMounted(true),100);return()=>clearTimeout(t);},[]);

  if(loading)return(
    <div className="py-pageload">
      <PoyaisSpinner size={38} pad={0} label={tt('VERİLER YÜKLENİYOR')}/>
    </div>
  );

  // ChatGPT Ads'in kendi API'sinde gelir/ROAS metriği yok (bkz. chatgpt_ads.py) — spend ve
  // conversions'ı toplama dahil, revenue'yu değil. Criteo/RTB House'da hepsi mevcut.
  const totalSpend=(gads?.spend||0)+(meta?.spend||0)+(chatgpt?.spend||0)+(criteo?.cost||0)+(rtbhouse?.total_spend||0);
  const totalRevenue=(gads?.revenue||0)+(meta?.revenue||0)+(criteo?.revenue||0)+(rtbhouse?.total_conv_value||0);
  const blendedRoas=totalSpend>0?totalRevenue/totalSpend:0;
  // Para birimleri: farklı birimler ASLA toplanmaz — her platformun rakamı kendi biriminde gruplanır (bilinmeyen birim ₺ varsayılır, eskiden olduğu gibi).
  const curOf=(k:string)=>((acctCtx?.currencies?.[k] as string|undefined)||'TRY');
  const platRows:[string,string,number,number][]=[['gads',curOf('gads'),gads?.spend||0,gads?.revenue||0],['meta',curOf('meta'),meta?.spend||0,meta?.revenue||0],
    ['chatgpt_ads',curOf('chatgpt_ads'),chatgpt?.spend||0,0],['criteo',curOf('criteo'),criteo?.cost||0,criteo?.revenue||0],['rtbhouse','TRY',rtbhouse?.total_spend||0,rtbhouse?.total_conv_value||0]];
  const byCur:Record<string,{spend:number;revenue:number}>={};
  platRows.forEach(([,c,sp,rv])=>{ if(!(sp||rv)) return; const e=byCur[c]||(byCur[c]={spend:0,revenue:0}); e.spend+=sp; e.revenue+=rv; });
  const curKeys=Object.keys(byCur);
  const mixedCur=curKeys.length>1;
  const baseCur=curKeys[0]||'TRY';
  const moneyK=(v:number,c:string)=>c==='TRY'?fmt_k(v):new Intl.NumberFormat(undefined,{style:'currency',currency:c,notation:'compact',maximumFractionDigits:1}).format(v);
  const groupedMoney=(f:(e:{spend:number;revenue:number})=>number)=>curKeys.map(c=>moneyK(f(byCur[c]),c)).join(' + ');
  const storeRev=acctCtx?.store_revenue;
  const blendedStore=(!mixedCur && storeRev && storeRev.revenue!=null && totalSpend>0 && (!storeRev.currency || storeRev.currency===baseCur)) ? storeRev.revenue/totalSpend : null;
  const metaOtherResults=((meta?.results||[]) as any[]).filter((r:any)=>r.result_type!=='PURCHASE' && r.count);
  // Brand/NonBrand ROAS — sadece Google+Meta kampanya listelerinden (brandSpend/brandRevenue),
  // ChatGPT/Criteo/RTB House hesaba katılmıyor çünkü onlar için ayrı kampanya listesi
  // çekilmiyor (bu kartların genel "overall" ROAS'ı zaten hepsini kapsıyor).
  const brandRoas=brandSpend>0?brandRevenue/brandSpend:0;
  const nonBrandRoas=nonBrandSpend>0?nonBrandRevenue/nonBrandSpend:0;
  const roasSegSpend=roasSegment==='brand'?brandSpend:roasSegment==='nonbrand'?nonBrandSpend:totalSpend;
  const roasSegValue=roasSegment==='brand'?brandRoas:roasSegment==='nonbrand'?nonBrandRoas:blendedRoas;
  const totalConversions=(gads?.conversions||0)+(meta?.purchases||0)+(chatgpt?.conversions||0)+(criteo?.conversions||0)+(rtbhouse?.total_conversions||0);
  // Reklam Harcaması/Gelir/Satın Alma kartlarının segment değerleri — ROAS'ınkiyle aynı
  // sınırlama: sadece Google+Meta kampanya listelerinden ayrılabiliyor (ChatGPT/Criteo/RTB
  // House için ayrı kampanya listesi çekilmiyor), o yüzden "Marka"+"Marka Dışı" toplamı
  // "Genel"e (totalSpend/totalRevenue/totalConversions) tam eşit olmayabilir — bu uydurma
  // değil, aynı önceden kabul edilmiş ROAS kısıtının devamı.
  const spendSegValue=spendSegment==='brand'?brandSpend:spendSegment==='nonbrand'?nonBrandSpend:totalSpend;
  const revenueSegValue=revenueSegment==='brand'?brandRevenue:revenueSegment==='nonbrand'?nonBrandRevenue:totalRevenue;
  const convSegValue=convSegment==='brand'?brandConversions:convSegment==='nonbrand'?nonBrandConversions:totalConversions;
  const prevBrandRoas=prevSeg.brandSpend>0?prevSeg.brandRevenue/prevSeg.brandSpend:0;
  const prevNonBrandRoas=prevSeg.nonBrandSpend>0?prevSeg.nonBrandRevenue/prevSeg.nonBrandSpend:0;
  const prevTotalSpend=(prevGads?.spend||0)+(prevMeta?.spend||0)+(prevChatgpt?.spend||0)+(prevCriteo?.cost||0)+(prevRtbhouse?.total_spend||0);
  const prevTotalRevenue=(prevGads?.revenue||0)+(prevMeta?.revenue||0)+(prevCriteo?.revenue||0)+(prevRtbhouse?.total_conv_value||0);
  const prevBlendedRoas=prevTotalSpend>0?prevTotalRevenue/prevTotalSpend:0;
  const prevTotalConversions=(prevGads?.conversions||0)+(prevMeta?.purchases||0)+(prevChatgpt?.conversions||0)+(prevCriteo?.conversions||0)+(prevRtbhouse?.total_conversions||0);
  const roasGoal=goals.find((g:any)=>g.metric==='roas'&&g.platform==='all')?.target ?? null;
  const convGoal=goals.find((g:any)=>g.metric==='conversions'&&g.platform==='all')?.target ?? null;

  // Kampanya Performansı grafiği: platform seçimi + nitelik seçimi kullanıcı kontrolünde.
  // hasRevenue=false olan platformlar (ChatGPT Ads/Adjust) o platformun kendi API'sinde
  // gelir/ROAS kavramı olmadığı için gelir/ROAS toplamlarına hiç girmiyor (uydurma değil).
  const KAMP_DEFS:Record<string,{label:string;color:string;monthly:any[];connected:boolean;hasRevenue:boolean}> = {
    gads:        {label:'Google Ads',  color:T.gold,   monthly:gadsMonthly,     connected:!!(gadsCfg.customerId && gadsCfg.enabled!==false),      hasRevenue:true},
    meta:        {label:'Meta Ads',    color:T.purple, monthly:metaMonthly,     connected:!!(metaCfg.accountId && metaCfg.enabled!==false),        hasRevenue:true},
    chatgpt_ads: {label:'ChatGPT Ads', color:T.green,  monthly:chatgptMonthly,  connected:!!(chatgptCfg.accountName && chatgptCfg.enabled!==false), hasRevenue:false},
    criteo:      {label:'Criteo',      color:T.blue,   monthly:criteoMonthly,   connected:!!(criteoCfg.advertiserName && criteoCfg.enabled!==false),hasRevenue:true},
    adjust:      {label:'Adjust',      color:T.amber,  monthly:adjustMonthly,   connected:!!(adjustCfg.appName && adjustCfg.enabled!==false),       hasRevenue:false},
    rtbhouse:    {label:'RTB House',   color:T.red,    monthly:rtbhouseMonthly, connected:!!(rtbhouseCfg.token && rtbhouseCfg.enabled!==false),     hasRevenue:true},
  };
  const kampConnectedKeys=Object.keys(KAMP_DEFS).filter(k=>KAMP_DEFS[k].connected);
  const kampActiveKeys=kampConnectedKeys.filter(k=>kampPlatforms.includes(k));
  // Ay anahtarları kendimiz üretiliyor (herhangi bir platformun kendi dizisine güvenmek
  // yerine) — gads/meta'nın eski /monthly'si veri olmayan ayları hiç döndürmeyebiliyor,
  // yeni platformlarınki her zaman 12 ayı sıfırla doldurup döndürüyor; ay bazında kendi
  // anahtarımızla eşleştirmek, index'e güvenmekten daha güvenli.
  const kampMonthKeys:string[]=(()=>{
    const keys:string[]=[];const now=new Date();
    for(let i=11;i>=0;i--){const dt=new Date(now.getFullYear(),now.getMonth()-i,1);keys.push(`${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-01`);}
    return keys;
  })();
  const kampByMonth:Record<string,Record<string,any>> = {};
  Object.keys(KAMP_DEFS).forEach(k=>{const m:Record<string,any>={};KAMP_DEFS[k].monthly.forEach((r:any)=>{if(r?.month)m[r.month]=r;});kampByMonth[k]=m;});
  const kampByDay:Record<string,Record<string,any>> = {};
  Object.keys(kampDailyByPlatform).forEach(k=>{const m:Record<string,any>={};(kampDailyByPlatform[k]||[]).forEach((r:any)=>{if(r?.date)m[r.date]=r;});kampByDay[k]=m;});
  const sliceMonthKeys=(range:ChartRange):string[]=>{
    if(range==='6m')return kampMonthKeys.slice(-6);
    return kampMonthKeys; // 'this'/'last' günlük moda geçiyor, ay listesine ihtiyaç yok
  };
  const kampIsDaily=isDailyRange(kampRange);
  const gaIsDaily=isDailyRange(gaRange);
  const visibleKampMonthKeys=sliceMonthKeys(kampRange);
  const visibleGaMonthKeys=sliceMonthKeys(gaRange);
  const kampDayKeys=kampIsDaily?enumerateDays(monthBounds(kampRange as 'this'|'last').start,monthBounds(kampRange as 'this'|'last').end):[];
  const gaDayKeys=gaIsDaily?enumerateDays(monthBounds(gaRange as 'this'|'last').start,monthBounds(gaRange as 'this'|'last').end):[];
  // Aynı toplama mantığı hem aylık (kampByMonth) hem günlük (kampByDay) tablo için
  // kullanılabiliyor — sadece hangi lookup'ın kullanılacağı parametre.
  const aggregateKamp=(byKey:Record<string,Record<string,any>>,key:string):number=>{
    if(kampMetric==='roas'){
      let sp=0,rev=0;
      kampActiveKeys.forEach(k=>{const row=byKey[k]?.[key];if(row){sp+=row.spend||0;if(KAMP_DEFS[k].hasRevenue)rev+=row.revenue||0;}});
      return sp>0?rev/sp:0;
    }
    if(kampMetric==='revenue'){
      let rev=0;
      kampActiveKeys.forEach(k=>{if(!KAMP_DEFS[k].hasRevenue)return;const row=byKey[k]?.[key];if(row)rev+=row.revenue||0;});
      return rev;
    }
    let v=0;
    kampActiveKeys.forEach(k=>{const row=byKey[k]?.[key];if(row)v+=row[kampMetric]||0;});
    return v;
  };
  const kampSeries:number[]=kampIsDaily
    ? kampDayKeys.map(key=>aggregateKamp(kampByDay,key))
    : visibleKampMonthKeys.map(key=>aggregateKamp(kampByMonth,key));
  const kampLabels=kampIsDaily
    ? kampDayKeys.map(key=>String(parseInt(key.split('-')[2],10)))
    : visibleKampMonthKeys.map(key=>monthNames(lang)[parseInt(key.split('-')[1],10)-1]);
  const KAMP_METRIC_LABELS:Record<string,string> = lang==='en'
    ? {spend:'Spend',conversions:'Conversions',roas:'ROAS',revenue:'Revenue',impressions:'Impressions'}
    : {spend:'Harcama',conversions:'Dönüşüm',roas:'ROAS',revenue:'Gelir',impressions:'Gösterim'};
  const kampFmt=(v:number)=>{
    if(kampMetric==='roas')return v.toFixed(2)+'x';
    if(kampMetric==='spend'||kampMetric==='revenue')return v>=1000000?'₺'+(v/1000000).toFixed(1)+'M':v>=1000?'₺'+(v/1000).toFixed(0)+'K':'₺'+Math.round(v);
    return v>=1000000?(v/1000000).toFixed(1)+'M':v>=1000?(v/1000).toFixed(1)+'K':String(Math.round(v));
  };
  const kampAxisFmt=(v:number)=>{
    if(kampMetric==='roas')return v.toFixed(1)+'x';
    if(kampMetric==='spend'||kampMetric==='revenue')return '₺'+Math.round(v/1000)+'K';
    return v>=1000?Math.round(v/1000)+'K':String(Math.round(v));
  };
  // Hangi metriğe (Harcama/Dönüşüm/ROAS/Gelir/Gösterim) basılırsa listedeki kampanyalar
  // ona göre büyükten küçüğe yeniden sıralanıyor — Analytics kartındaki kanal listesiyle
  // aynı desen.
  const sortedCampaigns=[...campaigns].sort((x,y)=>(y as any)[kampMetric]-(x as any)[kampMetric]).slice(0,4);

  // Analytics kartı (GA4) — aynı kampMonthKeys üzerinden, tek platform olduğu için
  // Kampanya Performansı'ndaki platform-toplama adımı gerekmiyor.
  const ga4ByMonth:Record<string,any> = {};
  ga4Monthly.forEach((r:any)=>{ if(r?.month) ga4ByMonth[r.month]=r; });
  const ga4ByDay:Record<string,any> = {};
  gaDaily.forEach((r:any)=>{ if(r?.date) ga4ByDay[r.date]=r; });
  const gaSeries:number[]=gaIsDaily
    ? gaDayKeys.map(key=>ga4ByDay[key]?.[gaMetric]||0)
    : visibleGaMonthKeys.map(key=>ga4ByMonth[key]?.[gaMetric]||0);
  const gaLabels=gaIsDaily
    ? gaDayKeys.map(key=>String(parseInt(key.split('-')[2],10)))
    : visibleGaMonthKeys.map(key=>monthNames(lang)[parseInt(key.split('-')[1],10)-1]);
  const GA_METRIC_LABELS:Record<string,string> = lang==='en'
    ? {sessions:'Sessions',purchases:'Orders',revenue:'Revenue',cr:'CR'}
    : {sessions:'Oturum',purchases:'Sipariş',revenue:'Gelir',cr:'CR'};
  const gaFmt=(v:number)=>{
    if(gaMetric==='cr')return v.toFixed(2)+'%';
    if(gaMetric==='revenue')return v>=1000000?'₺'+(v/1000000).toFixed(1)+'M':v>=1000?'₺'+(v/1000).toFixed(0)+'K':'₺'+Math.round(v);
    return v>=1000000?(v/1000000).toFixed(1)+'M':v>=1000?(v/1000).toFixed(1)+'K':String(Math.round(v));
  };
  const gaAxisFmt=(v:number)=>{
    if(gaMetric==='cr')return v.toFixed(1)+'%';
    if(gaMetric==='revenue')return '₺'+Math.round(v/1000)+'K';
    return v>=1000?Math.round(v/1000)+'K':String(Math.round(v));
  };
  const ga4Connected=!!(ga4Cfg.propertyId && ga4Cfg.enabled!==false);
  // Kampanya Performansı'ndaki "ilgili kampanyalar" listesiyle aynı desen: seçili tarih
  // aralığındaki varsayılan kanal grubu (sessionDefaultChannelGroup) dağılımı, tıklanan
  // metriğe (Oturum/Sipariş/Gelir/CR) göre büyükten küçüğe sıralanıyor. Sol kart 4 kampanya
  // gösterdiği için burada da hizalı dursun diye 5 kanal gösteriliyor.
  const sortedChannels=ga4Channels.map((c:any)=>({channel:c.channel,sessions:c.sessions||0,purchases:c.purchases||0,revenue:c.revenue||0,
    cr:c.sessions>0?(c.purchases||0)/c.sessions*100:0}))
    .sort((x,y)=>(y as any)[gaMetric]-(x as any)[gaMetric]).slice(0,5);
  const allChannelsConnected=CONNECT_PLATFORMS.every(p=>bagliPlatformlar.includes(p.key));

  return(
    <div style={{opacity:mounted?1:0,transition:'opacity 0.4s ease',display:'flex',flexDirection:'column',gap:16,fontFamily:FONT}}>

      {(!allChannelsConnected || !connectBannerDismissed) && (
        <ConnectBanner bagliPlatformlar={bagliPlatformlar} recsCount={recs.length} onNavigate={onNavigate} onDismiss={dismissConnectBanner}/>
      )}

      <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',gap:18,flexWrap:'wrap'}}>
        <div>
          <h1 style={{margin:0,fontFamily:SERIF,fontSize:'clamp(30px,3.4vw,40px)',fontWeight:400,letterSpacing:'-0.015em',lineHeight:1.1}}>
            {greetingWord(lang)}, {config?.profile?.name||tt('Kullanıcı')}
          </h1>
          <p style={{margin:'9px 0 0',fontSize:13.5,color:'var(--py-text-mid-alt)'}}>{lang==='en'
            ? `${config?.profile?.company_name||"Your account's"} marketing summary for today.`
            : `${config?.profile?.company_name||'Hesabının'} bugünkü pazarlama özeti.`}</p>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{display:'inline-flex',alignItems:'center',gap:7,fontSize:12,fontWeight:700,color:'var(--py-green-text)',background:'var(--py-green-tint)',padding:'7px 13px',borderRadius:999}}>
            <span style={{width:7,height:7,borderRadius:'50%',background:'var(--py-green)',display:'inline-block',animation:'pdot 2s infinite'}}/>{tt('Canlı Veri')}
          </span>
          {onDateChange ? (
            <DateFilterBox start={start} end={end} onChange={onDateChange}/>
          ) : (
            <div style={{display:'flex',alignItems:'center',gap:9,border:`1px solid ${T.borderMid}`,background:'var(--py-surface)',borderRadius:11,padding:'10px 14px',fontSize:13,fontWeight:600,color:'var(--py-text)'}}>
              <Icon.calendar size={16} color="var(--py-text-mid-alt)" sw={1.9}/>{start} – {end}
            </div>
          )}
        </div>
      </div>

      {/* KPI satırı — Performans Özeti + 4 metrik kartı */}
      {(gads||meta||chatgpt||criteo||rtbhouse)&&(
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))',gap:14}}>
        {!sidebarOpen && (
        <div style={{border:'1px solid var(--py-border)',borderRadius:14,background:'var(--py-surface)',padding:'18px 19px',display:'flex',flexDirection:'column'}}>
          <div style={{display:'flex',alignItems:'center',gap:9,marginBottom:12}}>
            <div style={{width:28,height:28,borderRadius:8,background:'color-mix(in srgb, var(--py-accent) 15%, var(--py-surface))',color:'var(--py-amber-deep-text)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <Icon.bolt size={15} sw={1.9}/>
            </div>
            <div style={{fontSize:10.5,fontWeight:800,letterSpacing:'.09em',textTransform:'uppercase' as const,color:'var(--py-text-mid-alt)',lineHeight:1.3}}>{lang==='en'?<>Performance<br/>Summary</>:<>Performans<br/>Özeti</>}</div>
          </div>
          <p style={{margin:'0 0 14px',fontSize:12.5,lineHeight:1.55,color:'var(--py-text-mid)'}}>{perfInsight||tt('Veriler toplandıkça buraya haftalık performans özeti gelecek.')}</p>
          <button onClick={()=>onNavigate?.('asistan')} style={{marginTop:'auto',display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,border:'1px solid var(--py-border-mid)',background:'var(--py-surface)',borderRadius:10,padding:'9px 12px',fontFamily:'inherit',fontSize:12.5,fontWeight:700,color:'var(--py-text)',cursor:'pointer'}}>
            <Icon.bolt size={14} sw={2} color="var(--py-amber-deep-text)"/>{tt('AI Asistana Sor')}
          </button>
        </div>
        )}

        {[
          {label:tt('Reklam Harcaması'),value:(mixedCur&&spendSegment==='overall')?groupedMoney(e=>e.spend):moneyK(spendSegValue,baseCur),icon:<Icon.chart size={15} sw={1.9}/>,bg:'var(--py-track-bg)',fg:'var(--py-text-mid)',cur:spendSegValue,prev:(mixedCur&&spendSegment==='overall')?0:spendSegment==='brand'?prevSeg.brandSpend:spendSegment==='nonbrand'?prevSeg.nonBrandSpend:prevTotalSpend,hint:spendSegment!=='overall'?((spendSegment==='brand'?tt('Marka'):tt('Marka Dışı'))+' · '+tt('Önceki döneme göre.')):tt('Önceki döneme göre.'),seg:true,segment:spendSegment,setSegment:setSpendSegment,menuOpen:spendMenuOpen,setMenuOpen:setSpendMenuOpen},
          {label:'ROAS',value:(mixedCur&&roasSegment==='overall')?'—':(roasSegSpend>0?fmt_roas(roasSegValue):'—'),icon:<Icon.target size={15} sw={1.9}/>,bg:'var(--py-purple-tint)',fg:'var(--py-purple-text)',cur:roasSegValue,prev:(mixedCur&&roasSegment==='overall')?0:roasSegment==='brand'?prevBrandRoas:roasSegment==='nonbrand'?prevNonBrandRoas:prevBlendedRoas,hint:roasSegment!=='overall'?((roasSegment==='brand'?tt('Marka'):tt('Marka Dışı'))+' · '+tt('Önceki döneme göre.')):(roasGoal?`${tt('Hedef')}: ${Number(roasGoal).toFixed(2)}x`:tt('Önceki döneme göre.')),seg:true,segment:roasSegment,setSegment:setRoasSegment,menuOpen:roasMenuOpen,setMenuOpen:setRoasMenuOpen},
          {label:tt('Reklam Geliri'),value:(mixedCur&&revenueSegment==='overall')?groupedMoney(e=>e.revenue):moneyK(revenueSegValue,baseCur),icon:<Icon.invoice size={15} sw={1.9}/>,bg:'var(--py-green-tint)',fg:'var(--py-green-text)',cur:revenueSegValue,prev:(mixedCur&&revenueSegment==='overall')?0:revenueSegment==='brand'?prevSeg.brandRevenue:revenueSegment==='nonbrand'?prevSeg.nonBrandRevenue:prevTotalRevenue,hint:revenueSegment!=='overall'?((revenueSegment==='brand'?tt('Marka'):tt('Marka Dışı'))+' · '+tt('Önceki döneme göre.')):tt('Önceki döneme göre.'),seg:true,segment:revenueSegment,setSegment:setRevenueSegment,menuOpen:revenueMenuOpen,setMenuOpen:setRevenueMenuOpen},
          {label:tt('Satın Alma'),value:fmt_n(convSegValue),icon:<Icon.checklist size={15} sw={1.9}/>,bg:'var(--py-amber-tint-alt)',fg:'var(--py-accent-deep)',cur:convSegValue,prev:convSegment==='brand'?prevSeg.brandConv:convSegment==='nonbrand'?prevSeg.nonBrandConv:prevTotalConversions,hint:convSegment!=='overall'?((convSegment==='brand'?tt('Marka'):tt('Marka Dışı'))+' · '+tt('Önceki döneme göre.')):(convGoal?`${tt('Hedef')}: ${fmt_n(Number(convGoal))}`:tt('Önceki döneme göre.')),seg:true,segment:convSegment,setSegment:setConvSegment,menuOpen:convMenuOpen,setMenuOpen:setConvMenuOpen},
        ].map((k:any,i)=>(
          <div key={i} style={{border:'1px solid var(--py-border)',borderRadius:14,background:'var(--py-surface)',padding:'18px 19px'}}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:14}}>
              {k.seg ? (
                <div style={{position:'relative'}}>
                  <button onClick={()=>k.setMenuOpen((o:boolean)=>!o)} style={{display:'flex',alignItems:'center',gap:3,border:'none',background:'none',padding:0,cursor:'pointer',fontFamily:'inherit'}}>
                    <span style={{fontSize:13,fontWeight:600,color:'var(--py-text-mid-alt)'}}>{k.label}</span>
                    <Icon.chevron size={11} sw={2.4} color="var(--py-text-mid-alt)"/>
                  </button>
                  {k.menuOpen && (<>
                    <div onClick={()=>k.setMenuOpen(false)} style={{position:'fixed',inset:0,zIndex:10}}/>
                    <div style={{position:'absolute',top:'100%',left:0,marginTop:6,background:'var(--py-surface)',border:'1px solid var(--py-border)',borderRadius:10,boxShadow:'0 8px 24px rgba(20,22,43,.14)',padding:4,zIndex:11,minWidth:126}}>
                      {(['overall','brand','nonbrand'] as const).map(s=>(
                        <button key={s} onClick={()=>{k.setSegment(s);k.setMenuOpen(false);}} style={{display:'block',width:'100%',textAlign:'left',border:'none',borderRadius:6,padding:'6px 10px',fontSize:12,fontWeight:600,fontFamily:'inherit',cursor:'pointer',background:k.segment===s?'var(--py-track-bg)':'transparent',color:'var(--py-text)'}}>
                          {s==='overall'?tt('Genel'):s==='brand'?tt('Marka'):tt('Marka Dışı')}
                        </button>
                      ))}
                    </div>
                  </>)}
                </div>
              ) : (
                <span style={{fontSize:13,fontWeight:600,color:'var(--py-text-mid-alt)'}}>{k.label}</span>
              )}
              <span style={{width:28,height:28,borderRadius:8,background:k.bg,color:k.fg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{k.icon}</span>
            </div>
            <div style={{fontSize:String(k.value).length>12?20:29,fontWeight:800,letterSpacing:'-.03em',fontFamily:MONO,fontVariantNumeric:'tabular-nums' as const}}>{k.value}</div>
            <div style={{display:'flex',alignItems:'center',gap:8,marginTop:12}}>
              {k.prev>0 && <DeltaPill value={fmt_pct(k.cur,k.prev)} dir={pct_dir(k.cur,k.prev)}/>}
              <span style={{fontSize:11.5,color:'var(--py-text-mid-alt)'}}>{k.hint}</span>
            </div>
          </div>
        ))}
      </div>
      )}

      {mixedCur && (
        <div style={{display:'flex',flexDirection:'column' as const,gap:6,border:'1px solid var(--py-border)',borderRadius:12,background:'var(--py-subtle-bg)',padding:'12px 16px',marginBottom:16,fontSize:12.5,color:'var(--py-text-mid-alt)',lineHeight:1.55}}>
          {mixedCur && <div><b style={{color:'var(--py-text)'}}>{tt('Para birimleri karışık — toplanmaz')}</b>: {curKeys.join(', ')}. {lang==='en'?'Each platform figure is shown in its own currency; ROAS is not computed across currencies.':'Her platformun rakamı kendi para biriminde gösterilir; farklı birimler arasında ROAS hesaplanmaz.'}</div>}
        </div>
      )}

      {/* Kampanya Performansı + Analytics */}
      <div style={{display:'flex',flexWrap:'wrap' as const,gap:16}}>
        <div style={{flex:'1 1 420px',minWidth:0,border:'1px solid var(--py-border)',borderRadius:16,background:'var(--py-surface)',padding:'20px 22px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:14,marginBottom:18}}>
            <div style={{fontSize:16.5,fontWeight:800,letterSpacing:'-.02em'}}>{tt('Kampanya Performansı')}</div>
            <span onClick={()=>onNavigate?.(gads?'gads-overview':'meta-overview')} style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12.5,fontWeight:700,color:'var(--py-amber-deep-text)',cursor:'pointer'}}>{tt('Tüm Kampanyalar')}<Icon.arrowRight size={14} sw={2.3}/></span>
          </div>
          <div style={{border:'1px solid var(--py-table-divider)',borderRadius:13,background:'var(--py-subtle-bg)',padding:'16px 18px 12px',marginBottom:16}}>
            {kampConnectedKeys.length===0 ? (
              <div style={{fontSize:12.5,color:'var(--py-text-dim)',padding:'24px 2px',textAlign:'center' as const}}>{tt("Bu grafiği görmek için Ayarlar'dan bir reklam platformu bağlayın.")}</div>
            ) : isMobile ? (
              <div style={{display:'flex',flexDirection:'column' as const,gap:10,marginBottom:14}}>
                <div style={{display:'flex',flexDirection:'column' as const,gap:6}}>
                  {kampConnectedKeys.map(k=>{
                    const def=KAMP_DEFS[k];
                    const on=kampPlatforms.includes(k);
                    return(
                      <div key={k} onClick={()=>kampToggle(k)}
                        style={{display:'flex',alignItems:'center',gap:10,padding:'9px 11px',borderRadius:10,cursor:'pointer',userSelect:'none' as const,
                        background:on?alpha(def.color,8):'var(--py-surface)',border:`1px solid ${on?alpha(def.color,33):'var(--py-divider)'}`}}>
                        <span style={{width:16,height:16,borderRadius:5,border:`1.6px solid ${on?def.color:'var(--py-border-soft)'}`,background:on?def.color:'transparent',
                          display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          {on&&<Icon.check size={11} sw={3} color="#fff"/>}
                        </span>
                        <span style={{fontSize:12.5,fontWeight:700,color:'var(--py-text)'}}>{def.label}</span>
                      </div>
                    );
                  })}
                </div>
                <select value={kampMetric} onChange={e=>kampSetMetric(e.target.value as any)}
                  style={{width:'100%',padding:'10px 12px',borderRadius:10,border:'1px solid var(--py-border-mid)',background:'var(--py-surface)',
                  fontSize:12.5,fontWeight:700,color:'var(--py-text)',fontFamily:'inherit'}}>
                  {(['spend','conversions','roas','revenue','impressions'] as const).map(mm=>(
                    <option key={mm} value={mm}>{KAMP_METRIC_LABELS[mm]}</option>
                  ))}
                </select>
                <RangeSwitcher value={kampRange} onChange={setKampRange}/>
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column' as const,gap:10,marginBottom:12}}>
                <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap' as const}}>
                  {kampConnectedKeys.map(k=>{
                    const def=KAMP_DEFS[k];
                    const on=kampPlatforms.includes(k);
                    return(
                      <span key={k}
                        onClick={()=>kampToggle(k)}
                        style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:11,fontWeight:700,
                        padding:'5px 10px',borderRadius:999,cursor:'pointer',userSelect:'none' as const,
                        color:on?'var(--py-text)':'var(--py-text-dim)',background:on?alpha(def.color,11):'var(--py-track-bg)',
                        border:`1px solid ${on?alpha(def.color,33):'var(--py-border-mid)'}`}}>
                        <span style={{width:8,height:8,borderRadius:3,background:on?def.color:'var(--py-border-soft)'}}/>{def.label}
                      </span>
                    );
                  })}
                </div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap' as const}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,background:'var(--py-track-bg)',borderRadius:9,padding:3,width:'fit-content'}}>
                    {(['spend','conversions','roas','revenue','impressions'] as const).map(mm=>(
                      <span key={mm} onClick={()=>kampSetMetric(mm)} style={{fontSize:10.5,fontWeight:700,padding:'5px 9px',borderRadius:7,cursor:'pointer',
                        color:kampMetric===mm?'var(--py-text)':'var(--py-text-mid-alt)',background:kampMetric===mm?'var(--py-surface)':'transparent',
                        boxShadow:kampMetric===mm?'0 1px 2px rgba(20,22,43,.1)':'none'}}>{KAMP_METRIC_LABELS[mm]}</span>
                    ))}
                  </div>
                  <RangeSwitcher value={kampRange} onChange={setKampRange}/>
                </div>
              </div>
            )}
            {kampConnectedKeys.length>0 && (<>
            <div style={{fontSize:11.5,color:'var(--py-text-mid-alt)',marginBottom:6}}>{RANGE_LABEL_LONG[kampRange]} · {KAMP_METRIC_LABELS[kampMetric]}{kampActiveKeys.length>1&&kampMetric==='roas'?' (blended)':kampActiveKeys.length>1?` · ${tt('Toplam')}`:''}</div>
            {kampActiveKeys.length===0?(
              <div style={{fontSize:12.5,color:'var(--py-text-dim)',padding:'24px 2px',textAlign:'center' as const}}>{tt('Grafiği görmek için en az bir platform seçin.')}</div>
            ):<MiniAreaChart data={kampSeries} color={T.gold} labels={kampLabels} fmt={kampFmt} axisFmt={kampAxisFmt}/>}
            </>)}
          </div>
          <div style={{display:'flex',flexDirection:'column' as const}}>
            {sortedCampaigns.length===0?(
              <div style={{fontSize:12.5,color:'var(--py-text-dim)',padding:'13px 2px'}}>{tt('Bu dönem için kampanya verisi yok.')}</div>
            ):sortedCampaigns.map((c,i)=>{
              const icon = c.platform==='gads' ? '/google-ads-icon.png' : '/meta-icon.png';
              return(
                <div key={i} style={{display:'flex',alignItems:'center',gap:14,padding:'13px 2px',borderTop:'1px solid var(--py-table-divider)'}}>
                  <span style={{width:26,height:26,borderRadius:7,background:'#fff',border:'1px solid var(--py-border)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <img src={icon} width={16} height={16} style={{objectFit:'contain' as const}} alt={c.platform==='gads'?'Google Ads':'Meta Ads'}/>
                  </span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13.5,fontWeight:700,whiteSpace:'nowrap' as const,overflow:'hidden',textOverflow:'ellipsis'}}>{c.name}</div>
                    <div style={{fontSize:11.5,color:'var(--py-text-mid-alt)'}}>{c.platform==='gads'?'Google Ads':'Meta Ads'} · {fmt_k(c.spend)} {tt('harcama')}</div>
                  </div>
                  <span style={{fontSize:13.5,fontWeight:800,color:kampMetric==='roas'?(c.roas>=4?'var(--py-green-text)':'var(--py-amber-text)'):'var(--py-text)',fontFamily:MONO}}>{kampFmt((c as any)[kampMetric])}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{flex:'1 1 420px',minWidth:0,border:'1px solid var(--py-border)',borderRadius:16,background:'var(--py-surface)',padding:'20px 22px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:14,marginBottom:18}}>
            <div style={{fontSize:16.5,fontWeight:800,letterSpacing:'-.02em'}}>{tt('Analytics')}</div>
            <span onClick={()=>onNavigate?.('ga4-overview')} style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12.5,fontWeight:700,color:'var(--py-amber-deep-text)',cursor:'pointer'}}>{tt('Detaylar')}<Icon.arrowRight size={14} sw={2.3}/></span>
          </div>
          <div style={{border:'1px solid var(--py-table-divider)',borderRadius:13,background:'var(--py-subtle-bg)',padding:'16px 18px 12px',marginBottom:16}}>
            {!ga4Connected ? (
              <div style={{fontSize:12.5,color:'var(--py-text-dim)',padding:'24px 2px',textAlign:'center' as const}}>{tt("Bu grafiği görmek için Ayarlar'dan Google Analytics'i bağlayın.")}</div>
            ) : isMobile ? (<>
              <div style={{display:'flex',flexDirection:'column' as const,gap:6,marginBottom:12}}>
                <select value={gaMetric} onChange={e=>gaSetMetric(e.target.value as any)}
                  style={{width:'100%',padding:'10px 12px',borderRadius:10,border:'1px solid var(--py-border-mid)',background:'var(--py-surface)',
                  fontSize:12.5,fontWeight:700,color:'var(--py-text)',fontFamily:'inherit'}}>
                  {(['sessions','purchases','revenue','cr'] as const).map(mm=>(
                    <option key={mm} value={mm}>{GA_METRIC_LABELS[mm]}</option>
                  ))}
                </select>
                <RangeSwitcher value={gaRange} onChange={setGaRange}/>
              </div>
              <div style={{fontSize:11.5,color:'var(--py-text-mid-alt)',marginBottom:6}}>{RANGE_LABEL_LONG[gaRange]} · {GA_METRIC_LABELS[gaMetric]}</div>
              <MiniAreaChart data={gaSeries} color={T.blue} labels={gaLabels} fmt={gaFmt} axisFmt={gaAxisFmt} height={140}/>
            </>) : (<>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap' as const,marginBottom:12}}>
                <div style={{display:'flex',alignItems:'center',gap:6,background:'var(--py-track-bg)',borderRadius:9,padding:3,width:'fit-content',flexWrap:'wrap' as const}}>
                  {(['sessions','purchases','revenue','cr'] as const).map(mm=>(
                    <span key={mm} onClick={()=>gaSetMetric(mm)} style={{fontSize:10.5,fontWeight:700,padding:'5px 9px',borderRadius:7,cursor:'pointer',
                      color:gaMetric===mm?'var(--py-text)':'var(--py-text-mid-alt)',background:gaMetric===mm?'var(--py-surface)':'transparent',
                      boxShadow:gaMetric===mm?'0 1px 2px rgba(20,22,43,.1)':'none'}}>{GA_METRIC_LABELS[mm]}</span>
                  ))}
                </div>
                <RangeSwitcher value={gaRange} onChange={setGaRange}/>
              </div>
              <div style={{fontSize:11.5,color:'var(--py-text-mid-alt)',marginBottom:6}}>{RANGE_LABEL_LONG[gaRange]} · {GA_METRIC_LABELS[gaMetric]}</div>
              <MiniAreaChart data={gaSeries} color={T.blue} labels={gaLabels} fmt={gaFmt} axisFmt={gaAxisFmt} height={140}/>
            </>)}
          </div>
          <div style={{display:'flex',flexDirection:'column' as const}}>
            {!ga4Connected||sortedChannels.length===0?(
              <div style={{fontSize:12.5,color:'var(--py-text-dim)',padding:'13px 2px'}}>{tt('Bu dönem için kanal verisi yok.')}</div>
            ):sortedChannels.map((c,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:14,padding:'13px 2px',borderTop:'1px solid var(--py-table-divider)'}}>
                <span style={{fontSize:13.5,fontWeight:700,whiteSpace:'nowrap' as const,overflow:'hidden',textOverflow:'ellipsis'}}>{c.channel}</span>
                <span style={{fontSize:13.5,fontWeight:800,color:'var(--py-text)',fontFamily:MONO,flexShrink:0}}>{gaFmt((c as any)[gaMetric])}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Hedef Takibi */}
      {(gads||meta)&&(
        <div style={{border:'1px solid var(--py-border)',borderRadius:16,background:'var(--py-surface)',padding:'20px 22px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:14,marginBottom:goalsOpen||goals.length>0?18:0}}>
            <div style={{fontSize:16.5,fontWeight:800,letterSpacing:'-.02em'}}>{tt('KPI Hedefleri')}</div>
            <button onClick={()=>{ if(!goalsOpen){setNewGoal({metric:'roas',target:'',platform:'all'});setEditingGoal(null);} setGoalsOpen(v=>!v); }} style={{border:'1px solid var(--py-border-mid)',background:'var(--py-surface)',borderRadius:10,padding:'8px 13px',fontFamily:'inherit',fontSize:12.5,fontWeight:700,color:'var(--py-text)',cursor:'pointer'}}>{goalsOpen?tt('Kapat'):tt('Hedef Ekle')}</button>
          </div>

          {goalsOpen&&(
            <div style={{border:'1px solid var(--py-table-divider)',borderRadius:12,background:'var(--py-subtle-bg)',padding:14,marginBottom:14,display:'flex',gap:10,flexWrap:'wrap' as const,alignItems:'flex-end'}}>
              <div style={{display:'flex',flexDirection:'column' as const,gap:4}}>
                <label style={{fontSize:10,fontWeight:600,color:'var(--py-text-mid-alt)'}}>{tt('Metrik')}</label>
                <select value={newGoal.metric} onChange={e=>setNewGoal(g=>({...g,metric:e.target.value}))}
                  style={{padding:'8px 10px',border:'1px solid var(--py-border)',borderRadius:7,fontSize:12,color:'var(--py-text)',background:'var(--py-surface)'}}>
                  <option value="roas">ROAS</option>
                  <option value="spend">{tt('Harcama (₺)')}</option>
                  <option value="conversions">{tt('Dönüşüm')}</option>
                  <option value="cpa">CPA (₺)</option>
                  <option value="ctr">CTR (%)</option>
                </select>
              </div>
              <div style={{display:'flex',flexDirection:'column' as const,gap:4}}>
                <label style={{fontSize:10,fontWeight:600,color:'var(--py-text-mid-alt)'}}>Platform</label>
                <select value={newGoal.platform} onChange={e=>setNewGoal(g=>({...g,platform:e.target.value}))}
                  style={{padding:'8px 10px',border:'1px solid var(--py-border)',borderRadius:7,fontSize:12,color:'var(--py-text)',background:'var(--py-surface)'}}>
                  <option value="all">{tt('Tümü')}</option>
                  <option value="google">Google Ads</option>
                  <option value="meta">Meta Ads</option>
                </select>
              </div>
              <div style={{display:'flex',flexDirection:'column' as const,gap:4}}>
                <label style={{fontSize:10,fontWeight:600,color:'var(--py-text-mid-alt)'}}>{tt('Hedef Değer')}</label>
                <input type="number" value={newGoal.target} onChange={e=>setNewGoal(g=>({...g,target:e.target.value}))}
                  placeholder={tt('Örn: 5')} style={{padding:'8px 10px',border:'1px solid var(--py-border)',borderRadius:7,fontSize:12,color:'var(--py-text)',background:'var(--py-surface)',width:100}}/>
              </div>
              <button onClick={async()=>{
                if(!newGoal.target) return;
                await axios.post('/api/v1/goals/',{metric:newGoal.metric,target:parseFloat(newGoal.target),platform:newGoal.platform});
                // Düzenleme sırasında metric/platform değiştiyse eski satır ayrı bir
                // conflict-key olduğu için silinmeden kalır — burada temizleniyor.
                if(editingGoal && (editingGoal.metric!==newGoal.metric || editingGoal.platform!==newGoal.platform)){
                  await axios.delete(`/api/v1/goals/${editingGoal.metric}?platform=${editingGoal.platform}`);
                }
                const r=await axios.get('/api/v1/goals/');setGoals(r.data);
                setNewGoal({metric:'roas',target:'',platform:'all'});setEditingGoal(null);setGoalsOpen(false);
              }} style={{background:T.accent,color:'#fff',border:'none',borderRadius:11,padding:'9px 16px',fontFamily:'inherit',fontSize:13,fontWeight:700,cursor:'pointer'}}>
                {tt('Kaydet')}
              </button>
            </div>
          )}

          {goals.length>0 && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:10}}>
              {goals.map((goal:any,i:number)=>{
                // Not: "google" dışındaki her platform değeri (özellikle 'all' — varsayılan,
                // yeni hedef oluşturulduğunda seçili) yanlışlıkla gads'a (Google Ads) düşüyordu
                // — sadece 'meta' özel olarak kontrol ediliyordu, 'all' hiç kontrol edilmiyordu.
                // Kullanıcı bunu yakaladı: KPI kartındaki ROAS (0.66, aslında gads'ın kendi
                // ROAS'ı) üstteki Genel ROAS kartıyla (1.30, gerçek blended) uyuşmuyordu; aynı
                // şekilde 'all' dönüşüm hedefi Meta/diğer platformların dönüşümlerini hiç
                // saymıyordu (sadece Google), bu yüzden düşük görünüyordu. 'spend' zaten doğru
                // yazılmıştı (meta/google/else-totalSpend) — diğerleri de aynı desene getirildi.
                let current = 0;
                if(goal.metric==='roas') current = goal.platform==='meta'?meta?.roas||0:goal.platform==='google'?gads?.roas||0:blendedRoas;
                else if(goal.metric==='spend') current = goal.platform==='meta'?meta?.spend||0:goal.platform==='google'?gads?.spend||0:totalSpend;
                else if(goal.metric==='conversions') current = goal.platform==='meta'?meta?.purchases||0:goal.platform==='google'?gads?.conversions||0:totalConversions;
                else if(goal.metric==='cpa') current = goal.platform==='meta'?(meta?.purchases>0?(meta?.spend||0)/meta.purchases:0):goal.platform==='google'?(gads?.conversions>0?(gads?.spend||0)/gads.conversions:0):(totalConversions>0?totalSpend/totalConversions:0);
                else if(goal.metric==='ctr') current = goal.platform==='meta'?meta?.ctr||0:goal.platform==='google'?gads?.ctr||0:(((gads?.impressions||0)+(meta?.impressions||0))>0?(((gads?.clicks||0)+(meta?.clicks||0))/((gads?.impressions||0)+(meta?.impressions||0))*100):0);

                const pct = goal.target>0?Math.min(100,Math.round((current/goal.target)*100)):0;
                // Her hedef kartı kendi sabit rengini alır (sarı/mavi/yeşil, sırayla döner) —
                // ilerlemeye göre kırmızı/yeşil değişimi yerine kartları birbirinden ayırt
                // etmek için. Çubuğun kendisi zaten Progress bileşeninde solundan (koyu, bu
                // renk) sağına (tint(color,40), açık) doğru gradyanlı — "koyudan açığa" bu
                // şekilde tek bir renk içinde sağlanıyor.
                const GOAL_COLORS = [T.amber, T.blue, T.green];
                const color = GOAL_COLORS[i % GOAL_COLORS.length];
                const metricLabels:any = lang==='en' ? {roas:'ROAS',spend:'Spend',conversions:'Conversions',cpa:'CPA',ctr:'CTR'} : {roas:'ROAS',spend:'Harcama',conversions:'Dönüşüm',cpa:'CPA',ctr:'CTR'};
                const platformLabels:any = lang==='en' ? {all:'All Platforms',google:'Google Ads',meta:'Meta Ads'} : {all:'Tüm Platformlar',google:'Google Ads',meta:'Meta Ads'};
                const fmt = (v:number) => goal.metric==='roas'?`${v.toFixed(2)}x`:goal.metric==='spend'||goal.metric==='cpa'?fmt_k(v):goal.metric==='ctr'?`${v.toFixed(1)}%`:fmt_n(v);

                return(
                  <div key={i} onClick={()=>{
                    setNewGoal({metric:goal.metric,target:String(goal.target),platform:goal.platform});
                    setEditingGoal({metric:goal.metric,platform:goal.platform});
                    setGoalsOpen(true);
                  }} style={{border:'1px solid var(--py-table-divider)',borderRadius:12,background:'var(--py-subtle-bg)',padding:14,cursor:'pointer'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:'var(--py-text)'}}>{metricLabels[goal.metric]||goal.metric}</div>
                        <div style={{fontSize:10,color:'var(--py-text-dim)'}}>{platformLabels[goal.platform]||goal.platform}</div>
                      </div>
                      <div style={{textAlign:'right' as const}}>
                        <div style={{fontSize:14,fontWeight:700,color,fontFamily:MONO}}>{fmt(current)}</div>
                        <div style={{fontSize:10,color:'var(--py-text-dim)'}}>{tt('Hedef')}: {fmt(goal.target)}</div>
                      </div>
                    </div>
                    <div style={{marginBottom:4}}><Progress pct={pct} color={color}/></div>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontSize:10,fontWeight:700,color}}>{pct}%</span>
                      <button onClick={async(e)=>{
                        e.stopPropagation();
                        await axios.delete(`/api/v1/goals/${goal.metric}?platform=${goal.platform}`);
                        const r=await axios.get('/api/v1/goals/');setGoals(r.data);
                      }} style={{fontSize:10,color:'var(--py-text-dim)',background:'none',border:'none',cursor:'pointer'}}>✕ {tt('Kaldır')}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Sinyaller + Görevler */}
      <div style={{display:'flex',flexWrap:'wrap' as const,gap:16,alignItems:'stretch' as const}}>
        <div style={{flex:'2 1 460px',minWidth:0,border:'1px solid var(--py-border)',borderRadius:16,background:'var(--py-surface)',padding:'20px 22px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:14,marginBottom:16}}>
            <div style={{fontSize:16.5,fontWeight:800,letterSpacing:'-.02em'}}>{tt('Sinyaller')}</div>
            <span onClick={()=>onNavigate?.('poyaisone-recommendations')} style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12.5,fontWeight:700,color:'var(--py-amber-deep-text)',cursor:'pointer'}}>{tt('Tüm Sinyaller')}<Icon.arrowRight size={14} sw={2.3}/></span>
          </div>
          <div style={{display:'flex',flexDirection:'column' as const,gap:10}}>
            {recs.length===0?(
              <div style={{fontSize:12.5,color:'var(--py-text-dim)',padding:'4px 0'}}>{tt('Şu an için sinyal yok.')}</div>
            ):recs.map((rec:any,i:number)=>{
              const sig=rec.priority==='high'?{bg:'var(--py-red-tint)',fg:'var(--py-red-text)',Ic:Icon.bolt}:rec.priority==='medium'?{bg:'var(--py-amber-tint-alt)',fg:'var(--py-amber-text)',Ic:Icon.target}:{bg:'var(--py-blue-tint)',fg:'var(--py-blue)',Ic:Icon.chart};
              return(
                <div key={i} onClick={()=>onNavigate?.('poyaisone-recommendations')} style={{display:'flex',gap:14,padding:'14px 15px',border:'1px solid var(--py-table-divider)',borderRadius:13,background:'var(--py-subtle-bg)',alignItems:'flex-start',cursor:'pointer'}}>
                  <div style={{width:34,height:34,borderRadius:10,flexShrink:0,background:sig.bg,color:sig.fg,display:'flex',alignItems:'center',justifyContent:'center'}}><sig.Ic size={17} sw={1.9}/></div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13.5,fontWeight:800,marginBottom:4}}>{rec.title||rec.action_type}</div>
                    <div style={{fontSize:12.5,color:'var(--py-text-mid-alt)',lineHeight:1.5}}>{rec.description||rec.reason||''}</div>
                    {rec.impact&&<div style={{fontSize:12,fontWeight:800,color:sig.fg,fontFamily:MONO,marginTop:7}}>{rec.impact}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{flex:'1 1 300px',minWidth:0,border:'1px solid var(--py-border)',borderRadius:16,background:'var(--py-surface)',padding:'20px 22px',display:'flex',flexDirection:'column' as const}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:14,marginBottom:16}}>
            <div style={{fontSize:16.5,fontWeight:800,letterSpacing:'-.02em'}}>{tt('Görevler')}</div>
            <span onClick={()=>onNavigate?.('gorevler')} style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12.5,fontWeight:700,color:'var(--py-amber-deep-text)',cursor:'pointer'}}>{tt('Tümünü Gör')}<Icon.arrowRight size={14} sw={2.3}/></span>
          </div>
          <div style={{display:'flex',flexDirection:'column' as const,gap:9}}>
            {miniTasks.length===0?(
              <div style={{fontSize:12.5,color:'var(--py-text-dim)',padding:'4px 0'}}>{tt('Bekleyen görev yok.')}</div>
            ):miniTasks.map((t:any)=>{
              const prio:Record<string,{bg:string;color:string}>={'Yüksek':{bg:'var(--py-red-tint)',color:'var(--py-red-text)'},'Orta':{bg:'var(--py-amber-tint-alt)',color:'var(--py-amber-text)'},'Düşük':{bg:'var(--py-track-bg)',color:'var(--py-text-mid)'}};
              const ps=prio[t.priority]||prio['Orta'];
              return(
                <div key={t.id} onClick={()=>onNavigate?.('gorevler')} style={{display:'flex',gap:11,alignItems:'flex-start',padding:'12px 13px',border:'1px solid var(--py-table-divider)',borderRadius:12,background:'var(--py-subtle-bg)',cursor:'pointer'}}>
                  <span style={{width:16,height:16,borderRadius:5,border:'1.8px solid var(--py-border-soft)',marginTop:1,flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,marginBottom:5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const}}>{t.title}</div>
                    <div style={{display:'flex',alignItems:'center',gap:7,flexWrap:'wrap' as const}}>
                      <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:5,background:ps.bg,color:ps.color}}>{tt(t.priority)}</span>
                      <span style={{fontSize:11,color:'var(--py-text-mid-alt)'}}>{openedLabel(t.created_at, lang, t.date_label)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{flex:1,minHeight:14}}/>
          <button onClick={()=>{
            axios.post(`${API}/gorevler/?client_id=${clientId||1}`,{status:'todo',priority:'Orta',title:'Yeni görev',date_label:'Bugün'})
              .then(r=>setMiniTasks((ts:any[])=>[r.data,...ts].slice(0,4))).catch(()=>{});
          }} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,border:'1px dashed var(--py-border-soft)',background:'transparent',borderRadius:11,padding:11,fontFamily:'inherit',fontSize:12.5,fontWeight:700,color:'var(--py-text-mid-alt)',cursor:'pointer'}}>
            {tt('Yeni Görev')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pdot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.3;transform:scale(.8)}}
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
      `}</style>
    </div>
  );
}
